import { pedirEsx } from '../lib/esxRequerido';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { avisarFalloDeCarga } from '../lib/fallos';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowDownUp,
  AlertTriangle, Archive, ArchiveRestore, Ban, Banknote, Bot, Building2, Calculator, Camera, Check,
  CheckCircle2, CheckSquare, Clapperboard, ClipboardList, Download, Eye, FileCheck2, FilePenLine, FileText,
  FolderOpen, HardHat, Link2, Mail, Pencil, Play, Puzzle, RotateCcw, Ruler, ScanText, Search, Square, Wrench, X,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { approveForRepair } from '../lib/approveRepair';
import { useI18n } from '../lib/i18n';
import { usePersistentState } from '../lib/usePersistentState';
import { useFoldState } from './FoldCard';
import { useAuth, hasRole } from '../lib/auth';
import { formatUsd, localeUI } from '../lib/format';
import { uploadFile } from '../lib/upload';
import { FileDrop } from './FileDrop';
import { FileViewerModal } from './FileViewerModal';
import { Avatar } from './Avatar';
import { useEstimateTagResolver } from '../lib/estimateTags';
import { EstimateReadingModal } from './EstimateReadingModal';
import { type ResumenLectura, colorSemaforo, fmtSegundos, lecturaEnCurso } from '../lib/estimateReading';
import { type ResumenLecturaPoliza, POLICY_TYPES, lecturaPolizaEnCurso } from '../lib/policyReading';

/** Una fila del cajón "Archivados": fuera de la lista, NO borrada. */
type ArchivadoRow = {
  id: string; file_name: string | null; doc_type: string; created_at: string;
  archivado_at: string; archivado_by: string | null; archivado_motivo: string | null;
};

type Validation = { validated: boolean; compared: number; diffs: Array<{ field: string; doc: string | null; claim: string | null }> } | null;
type Financials = {
  is_ours: boolean;
  rcv_amount_cents: number | null;
  deductible_cents: number | null;
  depreciation_cents: number | null;
  net_claim_cents: number | null;
  building_code: string | null;
  price_list: string | null;
  price_list_current: boolean | null;
  price_list_months_old: number | null;
  roof_full_replacement: boolean | null;
  /** Approved for REPAIR: etiqueta amarilla — NO cuenta como aprobación full. */
  is_repair?: boolean;
  repair_by?: string | null;
  repair_by_id?: string | null;
  /** Quién corrigió la etiqueta a mano. Ausente/null = la puso la regla, sin autor. */
  tag_by?: string | null;
  tag_by_id?: string | null;
  tag_at?: string | null;
  tag_action?: string | null;
  /** Cuándo se puso la etiqueta repair (tooltip "quién y cuándo"). */
  repair_at?: string | null;
  /** Id de la versión en `estimates`: necesario para corregir la etiqueta. */
  estimate_id?: string | null;
} | null;
type FileRow = {
  id: string;
  file_name: string | null;
  doc_type: string;
  doc_label: string | null;
  ai_summary: string | null;
  validation: Validation;
  created_at: string;
  uploaded_by: string | null;
  uploaded_by_user_id?: string | null;
  source_tag: string | null;
  imported: boolean;
  partner_visibility?: 'shared' | 'internal';
  /** Qué ve el CLIENTE en su portal. null = el portal está apagado y la casilla no se pinta. */
  portal?: { visible: boolean; motivo: string | null; manual: boolean } | null;
  esx: { id: string; file_name: string | null } | null;
  amount_cents: number | null;
  financials: Financials;
  atr_status?: string | null;
  external_url?: string | null;
  notes?: string | null;
  notes_by?: string | null;
  notes_at?: string | null;
  // CÓMO LLEGÓ (mig 0401). Se guarda al insertar; ver `comoLlego`.
  origin_kind?: string | null;
  origin_detail?: string | null;
  origin_email_id?: string | null;
  origin_screen?: string | null;
  /** Documento de la CASA (W-9, licencia del PA): va a su propia pestaña. */
  es_generico?: boolean;
  supplement_status?: { kind: 'resolved' | 'pre_approved'; at: string; by: string | null; by_user_id?: string | null } | null;
  /** Lectura única del estimado (mig 0498): estado por archivo; null si nunca se leyó. */
  reading?: ResumenLectura | null;
  /** Lectura única de la póliza (mig 0504): estado por archivo tipado "policy". */
  policy_reading?: ResumenLecturaPoliza | null;
};

const ESTIMATE_TYPES = ['estimate', 'proactive_estimate', 'mitigation_estimate'];

/** ¿Este file parece el contrato/LOR de Proactive? El clasificador no tiene un tipo propio para
 *  contratos: los LOR importados de Monday suelen quedar como 'other'. Así que ofrecemos "Analizar
 *  contrato" en todo lo que no sea claramente un estimado/COC/foto (tipo/label/nombre o 'other'). */
function looksLikeContract(f: FileRow): boolean {
  if (['contract', 'signed_contract', 'other'].includes(f.doc_type)) return true;
  const hay = `${f.doc_label ?? ''} ${f.file_name ?? ''}`.toLowerCase();
  return /contract|\blor\b|letter of rep|representation/.test(hay);
}

/** Pill de dato financiero: etiqueta chica + valor en bold, con color de fondo. */
function FinPill({ label, value, bg, fg }: { label: string; value: string; bg: string; fg: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5, background: bg, color: fg, borderRadius: 7, padding: '3px 9px', fontSize: 'var(--fs-2xs)' }}>
      <span style={{ opacity: 0.85, fontWeight: 600 }}>{label}</span>
      <strong style={{ fontWeight: 800, fontFamily: 'var(--font-mono)' }}>{value}</strong>
    </span>
  );
}

/**
 * Categoría VISUAL del archivo: da el COLOR de cada fila.
 *
 * Ya NO agrupa ni ordena — la lista va por fecha de subida (ver el render). El color es lo que
 * queda del tipo, y alcanza: dice de qué es el archivo sin obligar a leer, y sin romper la
 * historia cronológica que es como el equipo lee esta pestaña.
 */
type FileCat = 'estimates' | 'coc' | 'contracts' | 'checks' | 'photos' | 'atr' | 'other';
const CAT_META: Record<FileCat, { labelKey: string; color: string; icon: LucideIcon }> = {
  estimates: { labelKey: 'files.catEstimates', color: '#3730a3', icon: Ruler },
  coc: { labelKey: 'files.catCoc', color: '#166534', icon: FileCheck2 },
  contracts: { labelKey: 'files.catContracts', color: '#7c3aed', icon: FileText },
  checks: { labelKey: 'files.catChecks', color: '#0369a1', icon: Banknote },
  photos: { labelKey: 'files.catPhotos', color: '#b45309', icon: Camera },
  atr: { labelKey: 'files.catAtr', color: '#9d174d', icon: Clapperboard },
  other: { labelKey: 'files.catOther', color: '#6b7280', icon: FolderOpen },
};
function fileCategory(f: FileRow): FileCat {
  const dt = f.doc_type || '';
  const name = (f.file_name ?? '').toLowerCase();
  if (ESTIMATE_TYPES.includes(dt) || dt === 'estimate_esx' || name.endsWith('.esx')) return 'estimates';
  if (dt === 'coc') return 'coc';
  if (dt === 'atr_video') return 'atr';
  if (/check|invoice|payment|receipt/.test(dt) || /check|invoice|receipt/.test(name)) return 'checks';
  if (/contract|signed_contract/.test(dt) || /contract|\blor\b|letter of rep/.test(`${f.doc_label ?? ''} ${name}`.toLowerCase())) return 'contracts';
  if (/\.(png|jpe?g|gif|webp|bmp|heic)$/i.test(name)) return 'photos';
  return 'other';
}

/**
 * Los tipos que una persona puede elegir a mano en el desplegable de cada fila (pedido
 * 2026-08-03: "que puedan cambiar el tipo de file… insurance carrier estimate o proactive estimate
 * o policy"). Mismo listado que la lista blanca del backend — si se agrega uno acá hay que
 * agregarlo allá o el PATCH lo rechaza.
 */
export const DOC_TYPES: Array<{ value: string; key: string }> = [
  { value: 'estimate', key: 'ft.estimate' },
  { value: 'proactive_estimate', key: 'ft.proactive_estimate' },
  { value: 'mitigation_estimate', key: 'ft.mitigation_estimate' },
  { value: 'rebuild_estimate', key: 'ft.rebuild_estimate' },
  { value: 'estimate_esx', key: 'ft.estimate_esx' },
  { value: 'coc', key: 'ft.coc' },
  { value: 'contract', key: 'ft.contract' },
  { value: 'policy', key: 'ft.policy' },
  { value: 'weather_report', key: 'ft.weather_report' },
  { value: 'roof_diagram', key: 'ft.roof_diagram' },
  { value: 'atr_video', key: 'ft.atr_video' },
  { value: 'job_photo', key: 'ft.job_photo' },
  { value: 'invoice', key: 'ft.invoice' },
  { value: 'contractor_invoice', key: 'ft.contractor_invoice' },
  { value: 'ho_bank_check', key: 'ft.ho_bank_check' },
  { value: 'email_attachment', key: 'ft.email_attachment' },
  { value: 'info_pending', key: 'ft.info_pending' },
  { value: 'other', key: 'ft.other' },
];
export const DOC_TYPE_KEY: Record<string, string> = Object.fromEntries(DOC_TYPES.map((d) => [d.value, d.key]));
/** Tipos PROPIOS de la empresa (ej. Tarp invoice) — se cargan una vez por sesión. */
export type CustomType = { value: string; label: string };

// ── LAS PESTAÑAS DE FILES ───────────────────────────────────────────────────
//
// Pedido del dueño (2026-08-21): "vamos a colocar pestañas en files, algo que diga invoices;
// primero lo primero que sea estimate, como los de todos los estimados… bien organizado".
//
// La lista cronológica completa NO se va: sigue siendo la pestaña "Todos", que es como el equipo
// lee la historia del claim (pedido de Paula 2026-08-10, y sigue valiendo). Las pestañas son para
// la otra pregunta, la que hoy obliga a leer 12 filas: "¿dónde está el estimado?", "¿dónde está
// la invoice?". Cada pestaña se muestra SOLO si tiene algo adentro — una pestaña vacía es una
// pregunta más, no una respuesta.

export type Pestana = 'estimates' | 'invoices' | 'contract' | 'coc' | 'reports' | 'photos' | 'email' | 'casa' | 'all';

/** Los motivos que escribe la limpieza automática. Lo que escribe una persona se muestra tal cual
 *  (por eso el map en vez de una key dinámica: `t()` devuelve la key cuando no la encuentra, así
 *  que un motivo libre saldría como "files.motivo_lo que sea" en la pantalla). */
const MOTIVO_KEY: Record<string, string | undefined> = {
  firma: 'files.motivoFirma',
  duplicado: 'files.motivoDuplicado',
  generico: 'files.motivoGenerico',
};

/** El orden en que se ven, el que pidió el dueño: los estimados primero, invoices después. */
const PESTANAS: Array<{ id: Pestana; labelKey: string; icon: LucideIcon }> = [
  { id: 'estimates', labelKey: 'files.tabEstimates', icon: Ruler },
  { id: 'invoices', labelKey: 'files.tabInvoices', icon: Banknote },
  { id: 'contract', labelKey: 'files.tabContract', icon: FileText },
  { id: 'coc', labelKey: 'files.tabCoc', icon: FileCheck2 },
  { id: 'reports', labelKey: 'files.tabReports', icon: ClipboardList },
  { id: 'photos', labelKey: 'files.tabPhotos', icon: Camera },
  { id: 'email', labelKey: 'files.tabEmail', icon: Mail },
  { id: 'casa', labelKey: 'files.tabCasa', icon: Building2 },
  { id: 'all', labelKey: 'files.tabAll', icon: FolderOpen },
];

/**
 * A qué pestaña va cada archivo. UN archivo, UNA pestaña (menos "Todos", donde están todos):
 * si el mismo papel apareciera en dos, contar deja de significar nada y volvemos a "está
 * repetido".
 *
 * El orden de los `if` ES la regla de desempate y va de lo más específico a lo más general.
 * `es_generico` va primero a propósito: el W-9 de la empresa tiene doc_type 'other' y por nombre
 * parecería "otro documento" — lo que lo define no es qué dice sino que es de la casa.
 *
 * DEVUELVE `null` CUANDO NO SE SABE, y eso es una decisión, no una omisión: el 60% de los
 * archivos de prod tiene doc_type 'other' (12.526 de 20.959). Meterlos a todos en una pestaña
 * "Otros" sería reconstruir el basurero con un título nuevo y un número grande al lado. Lo que no
 * se puede nombrar vive en "Todos" —donde igual está— hasta que alguien le ponga el tipo con el
 * desplegable de la fila, y entonces aparece solo en su pestaña.
 */
export function pestanaDe(f: FileRow): Exclude<Pestana, 'all'> | null {
  const dt = f.doc_type || '';
  const name = (f.file_name ?? '').toLowerCase();
  const etiqueta = `${f.doc_label ?? ''} ${name}`.toLowerCase();
  if (f.es_generico) return 'casa';
  if (ESTIMATE_TYPES.includes(dt) || dt === 'estimate_esx' || dt === 'rebuild_estimate' || name.endsWith('.esx')) return 'estimates';
  if (dt === 'coc' || /certificate of completion|completion photo/.test(etiqueta)) return 'coc';
  if (/invoice|check|payment|receipt/.test(dt) || /invoice|receipt|\bcheck\b/.test(etiqueta)) return 'invoices';
  if (/contract|signed_contract|policy/.test(dt) || /contract|\blor\b|letter of rep|dec page|declaration/.test(etiqueta)) return 'contract';
  if (/weather_report|roof_diagram|atr_video|roof_measurement/.test(dt) || /weather|wind[-_ ]?report|hail report|measurement/.test(etiqueta)) return 'reports';
  if (dt === 'job_photo' || /\.(png|jpe?g|gif|webp|bmp|heic)$/i.test(name)) return 'photos';
  if (dt === 'email_attachment') return 'email';
  return null;
}

/** CÓMO LLEGÓ este archivo, en una frase. La pregunta textual del dueño: "quiero ver cómo llegó
 *  ese file, si llegó por un email, si alguien lo subió y quién lo hizo". */
export function comoLlego(
  f: FileRow, t: (k: string, vars?: Record<string, string>) => string,
): { icon: LucideIcon; texto: string; emailId: string | null } {
  const detalle = (f.origin_detail ?? '').trim();
  switch (f.origin_kind) {
    case 'email':
      return { icon: Mail, texto: t('files.origEmail', { de: detalle || '—' }), emailId: f.origin_email_id ?? null };
    case 'jobflow':
      return { icon: HardHat, texto: detalle || t('files.origJobflow'), emailId: null };
    case 'monday':
      return { icon: Download, texto: t('files.origMonday'), emailId: null };
    case 'link':
      return { icon: Link2, texto: t('files.origLink'), emailId: null };
    case 'system':
      return { icon: Bot, texto: t('files.origSystem'), emailId: null };
    case 'upload':
    default: {
      // Sin quién, "lo subió alguien" es la verdad y no una frase de relleno: los archivos
      // viejos importados antes de la migración 0401 no tienen a quién señalar.
      const quien = f.uploaded_by ?? null;
      const pantalla = f.origin_screen && f.origin_screen !== 'files' ? f.origin_screen : null;
      // Sin nombre no se escribe "lo subió —": eso no es información, es un guion. Los archivos
      // viejos (y los de un usuario que ya no está) no tienen a quién señalar, y decirlo es más
      // útil que fingir que sí.
      if (!quien) return { icon: FolderOpen, texto: t('files.origDesconocido'), emailId: null };
      return {
        icon: FolderOpen,
        texto: pantalla
          ? t('files.origUploadDesde', { quien: quien ?? '—', pantalla })
          : t('files.origUpload', { quien: quien ?? '—' }),
        emailId: null,
      };
    }
  }
}

/**
 * Módulo Files inteligente: drag & drop multi, cada archivo se sube a R2 y la IA
 * lo clasifica (estimate/coc/other). Los estimates disparan el parse de Payments
 * (quedan numerados en Finance); los COC se validan contra el claim.
 */
export function ClaimFiles({ claimId, onEstimate, onChanged, uploadOnly = false, estimatesOnly = false, startCollapsed = false, readOnly = false, titulo }: { claimId: string; onEstimate?: () => void; onChanged?: () => void; uploadOnly?: boolean; estimatesOnly?: boolean;
  /** Sin subir nada (ni archivo, ni link, ni ATR): "Previous claim files" muestra los archivos del
   *  claim ANTERIOR y lo nuevo tiene que ir al claim nuevo, no al viejo. */
  readOnly?: boolean; titulo?: string; /** Arranca plegado (Claim Review: la lista de archivos tapaba la decisión). */ startCollapsed?: boolean; /** Ya no hace nada: `wide` partía las tarjetas en dos columnas y ahora son filas. Se sigue aceptando para no tocar los seis llamadores. */ wide?: boolean }) {
  const { t } = useI18n();
  const { me } = useAuth();
  const canApprove = hasRole(me, 'office');
  // Etiqueta única del estimado (approved / round N / counter N…) — misma fuente que
  // Review Estimate. Reporte Wilcher 2026-08-18: los del seguro salían sin etiqueta en Files.
  const [tagsTick, setTagsTick] = useState(0);
  const etDe = useEstimateTagResolver(claimId, t, tagsTick);
  /* Es de las secciones más altas del claim: entra al mismo sistema de plegado que las demás
     (memoria por tipo de lista + "Plegar todo" de la pestaña). `startCollapsed` sigue siendo el
     valor de arranque la primera vez; después manda lo que el usuario dejó. */
  const [abierto, setAbierto] = useFoldState(`files:${estimatesOnly ? 'estimates' : uploadOnly ? 'upload' : startCollapsed ? 'review' : 'all'}`, !startCollapsed);
  const plegado = !abierto;
  const [allFiles, setAllFiles] = useState<FileRow[]>([]);
  // PESTAÑA ACTIVA. Arranca en Estimates si el claim tiene estimados —que es lo que se viene a
  // buscar el 90% de las veces— y si no, en Todos. `estimatesOnly` (Claim Review, la tarjeta de
  // First Estimate) ya venía filtrado desde antes y no muestra pestañas: ahí Files es una lista
  // de estimados y nada más.
  const [pestana, setPestana] = useState<Pestana>('all');
  // ORDEN POR FECHA (pedido del dueño 2026-09-11): "newest to oldest" / "oldest to newest", un botón
  // que alterna. Se recuerda por navegador. Solo aplica cuando la lista va por fecha: en el modo por
  // secuencia de negociación manda la ronda, no la fecha.
  const [masNuevoPrimero, setMasNuevoPrimero] = usePersistentState<boolean>('files:newestFirst', true);
  // En Estimados el orden es la SECUENCIA de la negociación (ronda 1, 2, 3…), del más viejo al más
  // nuevo. El mismo botón la invierte (pedido 2026-09-16: "change the files from newest to oldest"
  // también ahí). Es una preferencia aparte para no tocar el orden por defecto de esa pestaña.
  const [estNuevoPrimero, setEstNuevoPrimero] = usePersistentState<boolean>('files:estNewestFirst', false);
  const [pestanaFijada, setPestanaFijada] = useState(false);
  const visibles = estimatesOnly ? allFiles.filter((f) => ESTIMATE_TYPES.includes(f.doc_type)) : allFiles;
  // Cuántos hay en cada pestaña: el número decide cuáles se muestran (una pestaña vacía es una
  // pregunta más, no una respuesta) y se pinta al lado del nombre.
  const conteos = new Map<Pestana, number>();
  for (const f of visibles) {
    const p = pestanaDe(f);
    if (p) conteos.set(p, (conteos.get(p) ?? 0) + 1);
  }
  conteos.set('all', visibles.length);
  const pestanasVisibles = PESTANAS.filter((x) => (conteos.get(x.id) ?? 0) > 0);
  // Si la pestaña activa se quedó sin archivos (se archivó el último, se cambió el tipo), cae a
  // Todos en vez de mostrar una lista vacía sin explicación.
  const pestanaActiva: Pestana = (conteos.get(pestana) ?? 0) > 0 ? pestana : 'all';
  const files = pestanaActiva === 'all' || estimatesOnly ? visibles : visibles.filter((f) => pestanaDe(f) === pestanaActiva);
  /** ¿Se ordena por la SECUENCIA de la negociación en vez de por fecha? Solo en Estimados. */
  const porSecuencia = pestanaActiva === 'estimates' && !estimatesOnly;
  // ARCHIVADOS: el cajón del pie. Se pide solo cuando se abre — nadie necesita contar basura
  // en cada carga de la pantalla.
  const [archivados, setArchivados] = useState<ArchivadoRow[] | null>(null);
  const [verArchivados, setVerArchivados] = useState(false);
  // El archivo que se señala en amarillo tras un intento de subir algo que ya estaba: sin esto,
  // "ya está guardado" es una frase que obliga a buscar a mano cuál de los 12 es.
  const [resaltado, setResaltado] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const nav = useNavigate();
  /** Por qué no se pudo abrir un estimado en la herramienta (por fila). */
  const [estErr, setEstErr] = useState<{ id: string; text: string } | null>(null);
  /**
   * ESTIMAR SOBRE ESTE ESTIMADO (dueño 2026-10-30: "en Files, un botón para estimar sobre ese estimado: abre ese estimado en el
   * Estimating Tool para borrar o agregar ítems"). Crea un borrador con las partidas de la lectura del archivo (nuestro o del
   * seguro) y abre la herramienta; el atrás vuelve a esta pestaña.
   */
  async function estimarDesde(f: { id: string; reading?: ResumenLectura | null }) {
    setBusy(f.id); setEstErr(null);
    try {
      const r = await api.post<{ draft: { id: string } }>(`/api/estimating/claims/${claimId}/drafts`, { from: 'reading', fileId: f.id });
      nav(`/claims/${claimId}/estimate/${r.draft.id}`, { state: { from: `/claims/${claimId}?tab=files` } });
    } catch (e) {
      const code = e instanceof ApiError ? e.code : '';
      setEstErr({ id: f.id, text: code === 'reading_required' ? t('files.estimateNeedsReading') : e instanceof ApiError ? e.message : 'Error' });
    } finally { setBusy(null); }
  }
  // Arrastrar un .esx sobre la fila de un estimado nuestro (Manuel 2026-09-28): id de la fila resaltada.
  const [esxDrag, setEsxDrag] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [urls, setUrls] = useState<Record<string, string>>({});
  // Tipos custom de la empresa (2026-08-10: "déjame agregar tipos, ej. Tarp invoice").
  const [customTypes, setCustomTypes] = useState<CustomType[]>([]);
  useEffect(() => {
    api.get<{ types: CustomType[] }>('/api/files/doc-types').then((r) => setCustomTypes(r.types)).catch(() => {});
  }, []);
  // VISOR MODAL (2026-08-10, reporte de Manuel: "no tengo manera de abrir los documentos,
  // especialmente los contratos"). El visor incrustado quedaba al FONDO de la fila expandida
  // (fuera de vista sin scrollear) y si la URL fallaba no decía nada. El modal ocupa la
  // pantalla, muestra el error si lo hay, y se abre también clickeando el NOMBRE del archivo.
  const [viewer, setViewer] = useState<{ id: string; name: string | null } | null>(null);
  // ══ LECTURA ÚNICA DEL ESTIMADO (mig 0498) ══ Pedido del dueño 2026-10-07: "ponme ese botón de
  // análisis en cada estimado para que lo tengamos y probarlos a ver qué tan rápido". El POST solo
  // ENCOLA (Cloudflare corta a los 100 s y una lectura tarda 30-180 s), así que el estado se sigue
  // por polling cada 4 s mientras haya alguna en curso —también si la disparó otra persona o la
  // subida misma— y la lista se recarga UNA vez al terminar, no en cada tick.
  const [lectura, setLectura] = useState<{ fileId: string; fileName: string | null } | null>(null);
  const [rdError, setRdError] = useState<string | null>(null);
  const [rdPosting, setRdPosting] = useState<string | null>(null);
  // A los que se les dejó de preguntar (6 min sin terminar): el servidor sigue; la pestaña no.
  const [rdRendidos, setRdRendidos] = useState<Set<string>>(new Set());
  // Cuándo apretó ESTA persona el botón (los segundos que ella vio correr) y cuándo se vio por
  // primera vez cada lectura en curso (el corte de los 6 min). Refs: no son render.
  const rdInicio = useRef<Record<string, number>>({});
  const rdPrimeraVez = useRef<Record<string, number>>({});
  // Reloj de 1 s solo mientras algo se lee: es lo que hace avanzar los segundos del chip.
  const [, setRdReloj] = useState(0);
  const flashRdError = (m: string) => { setRdError(m); window.setTimeout(() => setRdError(null), 6000); };
  const rdEnCurso = allFiles.filter((f) => lecturaEnCurso(f.reading) && !rdRendidos.has(f.id)).map((f) => f.id);
  const rdEnCursoKey = rdEnCurso.join(',');
  useEffect(() => {
    if (!rdEnCurso.length) return;
    const ahora = Date.now();
    for (const id of rdEnCurso) rdPrimeraVez.current[id] ??= ahora;
    let vivo = true;
    const tick = async () => {
      let r: { readings: ResumenLectura[] };
      try { r = await api.get<{ readings: ResumenLectura[] }>(`/api/claims/${claimId}/readings`); } catch { return; }
      if (!vivo) return;
      const porId = new Map(r.readings.map((x) => [String(x.file_id), x]));
      setAllFiles((prev) => prev.map((f) => (porId.has(f.id) ? { ...f, reading: porId.get(f.id)! } : f)));
      const terminadas = rdEnCurso.filter((id) => { const n = porId.get(id); return !!n && !lecturaEnCurso(n); });
      const vencidas = rdEnCurso.filter((id) => !terminadas.includes(id) && Date.now() - (rdPrimeraVez.current[id] ?? ahora) > 6 * 60_000);
      if (vencidas.length) { setRdRendidos((s) => new Set([...s, ...vencidas])); flashRdError(t('rd.timeout')); }
      // Recarga ÚNICA al terminar: la lista trae el monto y las etiquetas que la lectura pudo cambiar. Y el PADRE
      // también (la lectura del estimado del seguro escribe los montos del claim): `onEstimate` es como ClaimDetail
      // vuelve a pedir el encabezado.
      if (terminadas.length) { await load(); onEstimate?.(); }
    };
    const id = window.setInterval(() => { void tick(); }, 4000);
    const reloj = window.setInterval(() => setRdReloj((n) => n + 1), 1000);
    return () => { vivo = false; window.clearInterval(id); window.clearInterval(reloj); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rdEnCursoKey, claimId]);

  // LECTURA ÚNICA DE LA PÓLIZA (mig 0504): mismo patrón que la del estimado, con su propia ruta y su propio reloj.
  const [pzPosting, setPzPosting] = useState<string | null>(null);
  const pzInicio = useRef<Record<string, number>>({});
  const pzPrimeraVez = useRef<Record<string, number>>({});
  const [pzRendidos, setPzRendidos] = useState<Set<string>>(new Set());
  const pzEnCurso = allFiles.filter((f) => lecturaPolizaEnCurso(f.policy_reading) && !pzRendidos.has(f.id)).map((f) => f.id);
  const pzEnCursoKey = pzEnCurso.join(',');
  useEffect(() => {
    if (!pzEnCurso.length) return;
    const ahora = Date.now();
    for (const id of pzEnCurso) pzPrimeraVez.current[id] ??= ahora;
    let vivo = true;
    const tick = async () => {
      let r: { readings: ResumenLecturaPoliza[] };
      try { r = await api.get<{ readings: ResumenLecturaPoliza[] }>(`/api/claims/${claimId}/policy-readings`); } catch { return; }
      if (!vivo) return;
      const porId = new Map(r.readings.filter((x) => x.claim_file_id).map((x) => [String(x.claim_file_id), x]));
      setAllFiles((prev) => prev.map((f) => (porId.has(f.id) ? { ...f, policy_reading: porId.get(f.id)! } : f)));
      const terminadas = pzEnCurso.filter((id) => { const n = porId.get(id); return !!n && !lecturaPolizaEnCurso(n); });
      const vencidas = pzEnCurso.filter((id) => !terminadas.includes(id) && Date.now() - (pzPrimeraVez.current[id] ?? ahora) > 8 * 60_000);
      if (vencidas.length) { setPzRendidos((s) => new Set([...s, ...vencidas])); flashRdError(t('pz.timeout')); }
      // AL TERMINAR, EL ENCABEZADO TAMBIÉN (Othilia Martins, 2026-10-19): la lectura escribe la TARJETA del claim
      // (policy type, deducible, building code, resumen) en el mismo instante en que el chip pasa a "N forms", pero
      // el chip se actualizaba solo y el encabezado quedaba viejo hasta recargar la página: parecía que la IA no
      // había llenado nada. `onEstimate` es el callback con el que ClaimDetail vuelve a pedir el claim.
      if (terminadas.length) { await load(); onEstimate?.(); }
    };
    const id = window.setInterval(() => { void tick(); }, 5000);
    const reloj = window.setInterval(() => setRdReloj((n) => n + 1), 1000);
    return () => { vivo = false; window.clearInterval(id); window.clearInterval(reloj); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pzEnCursoKey, claimId]);
  async function leerPoliza(f: FileRow, force: boolean) {
    if (force && f.policy_reading?.status === 'done' && !window.confirm(t('pr.rereadConfirm'))) return;
    setPzPosting(f.id);
    try {
      const r = await api.post<{ reading: ResumenLecturaPoliza }>(`/api/files/${f.id}/policy-reading`, { force });
      pzInicio.current[f.id] = Date.now();
      delete pzPrimeraVez.current[f.id];
      setPzRendidos((s) => { if (!s.has(f.id)) return s; const n = new Set(s); n.delete(f.id); return n; });
      setAllFiles((prev) => prev.map((x) => (x.id === f.id ? { ...x, policy_reading: r.reading } : x)));
    } catch (e) { flashRdError(e instanceof ApiError ? e.message : 'Error'); }
    finally { setPzPosting(null); }
  }

  /** Segundos que lleva ESTA lectura: desde el clic si fue acá; si no, desde que el servidor arrancó. */
  function rdSegundos(f: FileRow): string | null {
    const desde = rdInicio.current[f.id] ?? (f.reading?.started_at ? Date.parse(f.reading.started_at) : rdPrimeraVez.current[f.id]);
    return desde ? fmtSegundos(Date.now() - desde) : null;
  }

  /** Encola la lectura (o la relectura con `force`). No toca `busy`: eso congela toda la pestaña. */
  async function leerEstimado(f: FileRow, force: boolean) {
    if (force && f.reading?.status === 'done' && !window.confirm(t('rd.rereadConfirm'))) return;
    setRdPosting(f.id);
    try {
      const r = await api.post<{ reading: ResumenLectura }>(`/api/files/${f.id}/reading`, { force });
      rdInicio.current[f.id] = Date.now();
      delete rdPrimeraVez.current[f.id];
      setRdRendidos((s) => { if (!s.has(f.id)) return s; const n = new Set(s); n.delete(f.id); return n; });
      setAllFiles((prev) => prev.map((x) => (x.id === f.id ? { ...x, reading: r.reading } : x)));
    } catch (e) { flashRdError(e instanceof ApiError ? e.message : 'Error'); }
    finally { setRdPosting(null); }
  }
  // Qué fila está DESPLEGADA (una sola a la vez): la vista colapsada es el punto — 20 archivos
  // abiertos a la vez es exactamente el ruido del que se quejaron.
  // VARIOS abiertos a la vez, no uno. Con 4+ estimados en el mismo claim, abrir el segundo
  // cerraba el primero y no había forma de compararlos (pedido de Payments 2026-08-04).
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const setExpandedId = (id: string | null) => setExpandedIds((prev) => {
    const n = new Set(prev);
    if (id == null) return new Set();
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  // Rename inline: id del file en edición + borradores de etiqueta y nombre.
  const [editId, setEditId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editName, setEditName] = useState('');
  const flash = (m: string) => { setNotice(m); setTimeout(() => setNotice(null), 4000); };
  /** Manda el archivo por correo al contratista o al homeowner (desde claims@, asunto en inglés). */
  async function compartir(f: FileRow, to: 'contractor' | 'homeowner') {
    const quien = to === 'contractor' ? t('files.emailContractor') : t('files.emailHomeowner');
    if (!window.confirm(`${quien}: ${f.file_name ?? ''}`)) return;
    setBusy(f.id);
    try {
      const r = await api.post<{ sent_to: string }>(`/api/files/${f.id}/share`, { to });
      flash(t('files.emailSent').replace('{to}', r.sent_to));
    } catch (e) {
      const m = e instanceof ApiError ? e.message : 'Error';
      flash(m === 'sin_correo_del_contratista' ? t('files.noContractorEmail') : m === 'sin_correo_del_homeowner' ? t('files.noHomeownerEmail') : m);
    } finally { setBusy(null); }
  }

  // CONFIRMACIÓN OBLIGATORIA de la clasificación de la IA (pedido 2026-08-14, caso GMG:
  // estimados nuestros compartidos al contratista como si fueran del seguro). Al subir,
  // cada archivo queda en esta lista hasta que el usuario confirme el tipo o lo corrija
  // del desplegable — el dropzone no vuelve hasta confirmar todo.
  const [pendingConfirm, setPendingConfirm] = useState<Array<{ id: string; fileName: string; fileKey: string; aiType: string; chosen: string }>>([]);
  async function confirmClassification(fileId: string) {
    const p = pendingConfirm.find((x) => x.id === fileId);
    if (!p) return;
    setBusy(t('files.savingType'));
    try {
      if (p.chosen !== p.aiType) await api.patch(`/api/files/${fileId}`, { docType: p.chosen });
      // Confirmado como "estimado del seguro" → recién AHORA corre el flujo de Payments
      // (numerado + Finance). Antes corría al subir, con la clasificación sin confirmar.
      if (p.chosen === 'estimate') {
        setBusy(`${t('files.parsingEstimate')} ${p.fileName}…`);
        try { await api.post(`/api/claims/${claimId}/estimates/parse`, { fileKey: p.fileKey, fileName: p.fileName }); onEstimate?.(); } catch { /* ignore */ }
      }
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); setBusy(null); return; }
    setBusy(null);
    setPendingConfirm((prev) => prev.filter((x) => x.id !== fileId));
    await load();
    // Un estimado NUESTRO recién confirmado exige su .ESX en el acto (Manuel 2026-10-05): por acá
    // es por donde se sube en Supplementing.
    if (p.chosen === 'proactive_estimate') { await pedirEsx(claimId); await load(); }
  }

  async function load() {
    try {
      const r = await api.get<{ files: FileRow[] }>(`/api/claims/${claimId}/files`);
      setAllFiles(r.files);
      // La pestaña de arranque se elige UNA sola vez, en la primera carga con datos: si se
      // recalculara en cada load, subir un archivo te sacaría de la pestaña donde estabas.
      if (!pestanaFijada && r.files.length > 0) {
        setPestana(r.files.some((f) => pestanaDe(f) === 'estimates') ? 'estimates' : 'all');
        setPestanaFijada(true);
      }
    } catch { /* ignore */ }
    // Cada recarga de la lista refresca también las etiquetas (archivo recién subido o
    // monto corregido cambian la ley) — sin esto el chip nuevo esperaba a un remount.
    setTagsTick((n) => n + 1);
    if (verArchivados) await cargarArchivados();
  }

  async function cargarArchivados() {
    try { setArchivados((await api.get<{ files: ArchivadoRow[] }>(`/api/claims/${claimId}/files/archived`)).files); }
    catch { setArchivados([]); avisarFalloDeCarga('archivedFiles'); }
  }

  /**
   * ARCHIVAR = sacarlo de la lista, NO borrarlo. El archivo sigue en R2 y en la base con su
   * huella y su historia; vuelve con un click desde el cajón del pie. Es la acción que la mayoría
   * de la gente quiere cuando aprieta la ✕ ("esto no sirve acá"), y la que no cuesta un documento
   * si se equivoca.
   */
  async function archivar(f: FileRow) {
    setBusy(t('common.saving'));
    try { await api.post(`/api/files/${f.id}/archive`, { archivar: true, motivo: null }); flash(t('files.archived')); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); await load(); }
  }

  async function desarchivar(id: string) {
    setBusy(t('common.saving'));
    try { await api.post(`/api/files/${id}/archive`, { archivar: false }); flash(t('files.unarchived')); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
    finally { setBusy(null); await load(); await cargarArchivados(); }
  }

  /**
   * USAR ESTE ARCHIVO EN PAYMENTS — le saca los números SIN volver a subirlo.
   *
   * Pedido del dueño (2026-08-21): "están descargando el estimado para subirlo por ahí otra vez
   * para que haga los números, y lo guarda otra vez como basura en los files". Acá el parse corre
   * sobre el archivo que YA está: no hay bajada, ni subida, ni fila nueva — y la versión del
   * estimado queda atada a ESTE archivo (las 300 versiones huérfanas de prod salieron justo de
   * no poder hacer eso).
   */
  async function usarEnPayments(f: FileRow) {
    setBusy(`${t('files.readingNumbers')} ${f.file_name ?? ''}…`);
    try {
      const r = await api.post<{ status?: 'done' | 'reading'; parsed: { rcvAmountCents: number | null }; aiError?: boolean; error?: string }>(
        `/api/files/${f.id}/parse-estimate`, {});
      // Sin lectura todavía: el servidor la encola y escribe los números solo al terminar (~2 min).
      if (r.status === 'reading') flash(t('files.numbersReading'));
      else if (r.aiError) flash(t('files.numbersFailed', { err: r.error ?? '' }));
      else flash(t('files.numbersRead', { monto: r.parsed?.rcvAmountCents != null ? formatUsd(r.parsed.rcvAmountCents) : '—' }));
      onEstimate?.(); onChanged?.();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); await load(); }
  }
  useEffect(() => { load(); }, [claimId]);

  /**
   * PONE o QUITA la etiqueta "Repair estimate" (office+). Casos Hong Phuc Thi Trinh y Londa
   * Amis (2026-08-19): la etiqueta estaba mal y al revés — "el que dice approved es el repair
   * y el que dice repair es approved". El backend la aplica a la VERSIÓN entera del estimado
   * (el mismo documento suele estar repetido en varias filas), y corregir el rótulo NO mueve
   * el claim de cola.
   */
  /**
   * Guarda la etiqueta puesta a mano. El valor del desplegable es 'auto' | 'repair' |
   * 'excluded' | 'n<N>' — la traducción a modo+número se hace acá y no en el servidor para que
   * el <select> tenga UN value por opción (con dos campos sueltos, volver a "automático" desde
   * un número dejaba el número escrito y la opción elegida decía otra cosa).
   */
  async function setEstimateTag(f: FileRow, valor: string) {
    const cuerpo = valor.startsWith('n')
      ? { mode: 'number', round: Number(valor.slice(1)) }
      : { mode: valor };
    setBusy(t('common.saving'));
    try {
      await api.put(`/api/files/${f.id}/estimate-tag`, cuerpo);
      flash(t('files.tagSaved'));
      onEstimate?.();
      onChanged?.();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
    finally { setBusy(null); await load(); }
  }

  // Full replacement → status 'Approved' (copia el cliente a Review Estimate). office+.
  async function approveFull() {
    setBusy(t('files.applying'));
    try { await api.post(`/api/claims/${claimId}/approve-insurance`, {}); flash(t('files.approvedFull')); onEstimate?.(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); await load(); }
  }
  // Reparación → mismo flujo que el botón de Payments (cola Approved for repair + espera cheque).
  async function approveRepair() {
    setBusy(t('files.applying'));
    try { await approveForRepair(claimId, t('afr.ask')); flash(t('files.approvedRepair')); onEstimate?.(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); await load(); }
  }
  // Send to review → Review Estimate (seguro = B vs nuestro A) sin marcar Approved. office+.
  async function sendToReview() {
    setBusy(t('files.applying'));
    try { await api.post(`/api/claims/${claimId}/supplements/to-review`, {}); flash(t('files.sentToReview')); onEstimate?.(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); await load(); }
  }

  async function handleFiles(list: File[]) {
    const yaEstaban: string[] = [];
    for (const file of list) {
      setBusy(`${t('files.uploading')} ${file.name}…`);
      try {
        const key = await uploadFile(file, 'misc');
        const r = await api.post<{ id: string; doc_type: string; estimate: boolean; duplicado_de?: { id: string; file_name: string | null } }>(`/api/claims/${claimId}/files`, {
          fileKey: key, fileName: file.name, mimeType: file.type || null, screen: 'files',
        });
        // ESE CONTENIDO YA ESTABA en el claim: el servidor no guardó una segunda fila. Se le
        // dice a la persona (si no, cree que su subida se perdió y la repite, que es exactamente
        // cómo se llegó a 2.516 filas duplicadas) y se le señala cuál es el archivo que ya está.
        if (r.duplicado_de) {
          yaEstaban.push(r.duplicado_de.file_name || file.name);
          setResaltado(String(r.duplicado_de.id));
          continue;
        }
        // La IA propone; la persona CONFIRMA. El archivo entra a la lista de confirmación y el
        // flujo de Payments (parse del estimado) corre DESPUÉS de confirmar — no antes: un
        // archivo mal clasificado creaba un estimado fantasma en Finance (caso GMG 2026-08-14).
        setPendingConfirm((prev) => [...prev, { id: String(r.id), fileName: file.name, fileKey: key, aiType: r.doc_type, chosen: r.doc_type }]);
      } catch (e) {
        alert(e instanceof ApiError ? e.message : 'Error');
      }
    }
    setBusy(null);
    if (yaEstaban.length) flash(t('files.dupSkipped', { n: String(yaEstaban.length), nombres: yaEstaban.join(', ') }));
    await load();
  }

  // Agrega un file por LINK (Drive/Dropbox/…) — p.ej. el ATR que el contratista manda como enlace.
  async function addLink() {
    const url = window.prompt(t('files.linkPrompt'));
    if (!url || !url.trim()) return;
    const name = window.prompt(t('files.linkNamePrompt'));
    if (!name || !name.trim()) return;
    setBusy(t('files.uploading'));
    try { await api.post(`/api/claims/${claimId}/link-file`, { url: url.trim(), name: name.trim() }); flash(t('files.linkSaved')); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); await load(); }
  }

  // REEMPLAZAR el archivo (llegó una versión corregida del CO): sube el nuevo sobre la misma fila.
  async function replaceFileUpload(f: FileRow, file: File) {
    setBusy(`${t('files.replacing')} ${file.name}…`);
    try {
      const key = await uploadFile(file, 'misc');
      await api.post(`/api/files/${f.id}/replace`, { fileKey: key, fileName: file.name });
      setUrls((m) => { const n = { ...m }; delete n[f.id]; return n; }); // invalida la URL cacheada
      flash(t('files.replaced'));
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); await load(); }
  }

  // Rename inline: guarda etiqueta + nombre del file.
  function startEdit(f: FileRow) {
    setEditId(f.id);
    setEditLabel(f.doc_label ?? '');
    setEditName(f.file_name ?? '');
  }
  async function saveEdit() {
    if (!editId) return;
    setBusy(t('files.renaming'));
    try { await api.patch(`/api/files/${editId}`, { fileName: editName, docLabel: editLabel }); setEditId(null); flash(t('files.renamed')); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); await load(); }
  }

  /** Cambia el TIPO del archivo desde el desplegable de la fila. */
  async function changeDocType(f: FileRow, docType: string) {
    if (docType === f.doc_type) return;
    setBusy(t('files.savingType'));
    // Optimista: el select tiene que responder en el acto, no después del round-trip.
    setAllFiles((prev) => prev.map((x) => (x.id === f.id ? { ...x, doc_type: docType, doc_label: null } : x)));
    let guardado = false;
    try { await api.patch(`/api/files/${f.id}`, { docType }); guardado = true; }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
    finally { setBusy(null); await load(); }
    // Reclasificado a estimado nuestro: también exige su .ESX.
    if (guardado && docType === 'proactive_estimate') { await pedirEsx(claimId); await load(); }
  }

  // Agrega el link del ATR (YouTube) como file del claim (marcado luego approved/rejected).
  async function addAtr() {
    const url = window.prompt(t('files.atrPrompt'));
    if (!url || !url.trim()) return;
    setBusy(t('files.uploading'));
    try { await api.post(`/api/claims/${claimId}/atr-video-file`, { url: url.trim() }); flash(t('files.atrSaved')); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); await load(); }
  }

  // Re-analiza con IA un archivo importado (de Monday) como si se subiera ahora:
  // la IA lo clasifica (nuestro estimado / seguro / COC…) y extrae el monto.
  // Compartir/ocultar el archivo con el CONTRATISTA (JobFlow). Por defecto todo se comparte;
  // esto sirve para ocultar un documento puntual (carta interna, nota del carrier) sin borrarlo.
  async function togglePartnerVisibility(f: FileRow) {
    const share = (f.partner_visibility ?? 'shared') === 'internal';
    setBusy(t('files.savingVisibility'));
    try {
      await api.post(`/api/files/${f.id}/partner-visibility`, { shared: share });
      await load();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(''); }
  }

  /**
   * Mostrar u ocultar ESTE archivo en el portal del CLIENTE (pedido del dueño 2026-10-14: "en files
   * ponme un check mark por si quiero que un specific file no se vea lo desmarco").
   *
   * La decisión queda con nombre y fecha y MANDA sobre la regla automática por tipo: desmarcar acá
   * esconde el archivo aunque su tipo esté tildado en Settings. Lo que no puede es abrir lo que el
   * veto duro cierra (cheques, grabaciones) — por eso el botón se deshabilita y dice el motivo.
   */
  async function togglePortalVisibility(f: FileRow) {
    if (!f.portal) return;
    setBusy(t('files.savingVisibility'));
    try {
      await api.patch(`/api/client-portal/files/${f.id}`, { visible: !f.portal.visible });
      await load();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(''); }
  }

  // Monto del estimado EDITABLE a mano (pedido 2026-08-17: "los montos de los estimados
  // quiero poder corregirlos"). Escribe en la fila de estimates ligada + ai_data del file.
  const [amtEditId, setAmtEditId] = useState<string | null>(null);
  const [amtEditVal, setAmtEditVal] = useState('');
  async function saveFileAmount(f: FileRow) {
    const cents = Math.round(parseFloat(amtEditVal.replace(/[$,\s]/g, '')) * 100);
    setAmtEditId(null);
    if (!Number.isFinite(cents) || cents <= 0) return;
    if (cents === (f.financials?.rcv_amount_cents ?? f.amount_cents)) return;
    setBusy(t('files.savingAmount'));
    try { await api.post(`/api/files/${f.id}/amount`, { amountCents: cents }); flash(t('files.amountSaved')); onEstimate?.(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
    finally { setBusy(null); await load(); }
  }

  async function reanalyze(f: FileRow) {
    setBusy(`${t('files.analyzing')} ${f.file_name ?? ''}…`);
    try {
      await api.post(`/api/files/${f.id}/reclassify`, {});
      flash(t('files.analyzed')); onEstimate?.();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); await load(); }
  }

  // Analiza este file como CONTRATO/LOR: la IA lee el PDF, extrae nombre/dirección/seguro/póliza/fecha
  // de firma y lo compara contra el claim. El resultado queda "analizado" en Claim Review.
  async function analyzeAsContract(f: FileRow) {
    setBusy(`${t('files.analyzing')} ${f.file_name ?? ''}…`);
    try {
      const r = await api.post<{ hasContract: boolean; checks: Array<{ status: string }>; advancedToSigned?: boolean }>(`/api/claims/${claimId}/contract/analyze-file`, { fileId: f.id });
      const issues = (r.checks || []).filter((c) => c.status !== 'match').length;
      if (r.advancedToSigned) flash(t('files.contractSignedMoved'));
      else flash(issues > 0 ? t('files.contractIssues', { n: String(issues) }) : t('files.contractOk'));
      onChanged?.(); // refresca la lista de Intake para que el claim se mueva a Contract Signed
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); }
  }

  // Re-analiza TODOS los importados del claim de una pasada.
  async function reanalyzeAll() {
    setBusy(t('files.analyzingAll'));
    try {
      const r = await api.post<{ count: number }>(`/api/claims/${claimId}/files/reclassify-all`, {});
      flash(`${r.count} ${t('files.analyzedN')}`); onEstimate?.();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); await load(); }
  }

  // Sube el .ESX enlazado a ESE estimado (queda organizado junto a él).
  async function uploadEstimateEsx(estimateId: string, file: File) {
    setBusy(`${t('files.uploading')} ${file.name}…`);
    try {
      const key = await uploadFile(file, 'estimates');
      await api.post(`/api/files/${estimateId}/estimate-esx`, { fileKey: key, fileName: file.name, mimeType: file.type || null });
      flash(t('files.esxSaved'));
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); await load(); }
  }

  const importedCount = files.filter((f) => f.imported).length;

  /** URL firmada (cacheada) del archivo. */
  async function urlFor(id: string): Promise<string | null> {
    if (urls[id]) return urls[id];
    try { const r = await api.get<{ url: string }>(`/api/files/${id}/url`); setUrls((m) => ({ ...m, [id]: r.url })); return r.url; }
    catch { return null; }
  }
  async function del(id: string) {
    if (!window.confirm(t('files.deleteConfirm'))) return;
    try { await api.del(`/api/files/${id}`); await load(); } catch { /* ignore */ }
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {/* PLEGAR TODO EL MÓDULO. En Claim Review un claim con 13 archivos empujaba el contrato,
            la póliza y los botones de decisión fuera de la pantalla: el módulo Files se comía la
            revisión entera. El título es el que pliega (y dice cuántos hay, para no tener que
            abrirlo solo para contarlos). */}
        <h2 className="card-title" style={{ margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          onClick={() => setAbierto(plegado)} title={plegado ? t('files.expand') : t('files.collapse')}>
          <span style={{ color: 'var(--muted)', fontSize: 'var(--fs-2xs)' }}>{plegado ? '▸' : '▾'}</span>
          {titulo ?? (estimatesOnly ? t('files.estimatesTitle') : t('cdw.files'))}
          {/* EL NÚMERO DEL TÍTULO ES EL TOTAL DEL CLAIM, no el de la pestaña abierta. Con el
              conteo filtrado, un claim con 14 archivos decía "Files 4" mientras mirabas
              Estimados — y el número del título es justo el que se usa para saber cuánto hay
              sin abrir nada. El de cada pestaña ya está en su propio botón. */}
          {visibles.length > 0 && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{visibles.length}</span>}
        </h2>
        <span style={{ flex: 1 }} />
        {/* Orden por fecha: alterna entre más nuevo primero y más viejo primero. */}
        {!plegado && files.length > 1 && (
          <button className="btn ghost sm" onClick={() => (porSecuencia ? setEstNuevoPrimero((v) => !v) : setMasNuevoPrimero((v) => !v))} title={t('files.sortHint')}>
            <ArrowDownUp size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {(porSecuencia ? estNuevoPrimero : masNuevoPrimero) ? t('files.newestFirst') : t('files.oldestFirst')}
          </button>
        )}
        {/* Con varios estimados, poder abrirlos todos de una es lo que permite compararlos. */}
        {!plegado && files.length > 1 && (
          <button className="btn ghost sm" onClick={() => setExpandedIds((prev) => (
            prev.size >= files.length ? new Set() : new Set(files.map((f) => f.id))
          ))}>
            {expandedIds.size >= files.length ? `▴ ${t('files.collapseAll')}` : `▾ ${t('files.expandAll')}`}
          </button>
        )}
        {!plegado && !estimatesOnly && !readOnly && <button className="btn ghost sm" disabled={!!busy} onClick={addLink}><Link2 size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.addLink')}</button>}
        {!plegado && !estimatesOnly && !readOnly && <button className="btn ghost sm" disabled={!!busy} onClick={addAtr}><Clapperboard size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.addAtr')}</button>}
      </div>
      {plegado ? null : <>
      {/* Mientras haya clasificaciones sin confirmar, el panel REEMPLAZA al dropzone:
          no se sube nada más hasta confirmar lo que la IA dijo de cada archivo. */}
      {pendingConfirm.length > 0 ? (
        <div style={{ border: '2px solid var(--orange)', background: 'var(--orange-soft, #fff7ed)', borderRadius: 10, padding: 12, display: 'grid', gap: 10 }}>
          <div style={{ fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-sm)' }}>{t('files.confirmTitle')}</div>
          {pendingConfirm.map((p) => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 'var(--fs-sm)', wordBreak: 'break-all' }}>{p.fileName}</span>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('files.confirmAiSays')}</span>
              <select className="input sm" style={{ minWidth: 200 }} value={p.chosen}
                onChange={(e) => setPendingConfirm((prev) => prev.map((x) => (x.id === p.id ? { ...x, chosen: e.target.value } : x)))}>
                {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{t(d.key)}</option>)}
                {customTypes.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                {!DOC_TYPES.some((d) => d.value === p.aiType) && !customTypes.some((x) => x.value === p.aiType) && (
                  <option value={p.aiType}>{p.aiType}</option>
                )}
              </select>
              <button className="btn sm" disabled={!!busy} onClick={() => void confirmClassification(p.id)}>
                {p.chosen === p.aiType ? t('files.confirmOk') : t('files.confirmFix')}
              </button>
            </div>
          ))}
        </div>
      ) : !estimatesOnly && !readOnly && <FileDrop
        onFile={(f) => handleFiles([f])}
        onFiles={handleFiles}
        multiple
        accept="application/pdf,image/*"
        label={t('files.drop')}
        busy={!!busy}
        busyLabel={busy ?? ''}
      />}
      {notice && (
        <div style={{ marginTop: 10, background: 'var(--green-soft)', border: '1px solid #86efac', color: 'var(--green)', borderRadius: 8, padding: '8px 12px', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' }}><Check size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {notice}</div>
      )}
      {/* Lo que la lectura del estimado NO pudo hacer (PDF protegido, muy largo, no es PDF…), en rojo
          y unos segundos: el verde de arriba es para lo que salió bien. */}
      {rdError && (
        <div style={{ marginTop: 10, background: 'var(--red-soft)', border: '1px solid color-mix(in srgb, var(--red) 40%, transparent)', color: 'var(--red)', borderRadius: 8, padding: '8px 12px', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' }}><AlertTriangle size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {rdError}</div>
      )}

      {!uploadOnly && importedCount > 0 && (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn sm" style={{ background: 'var(--accent)', color: '#fff', borderColor: 'transparent' }} disabled={!!busy} onClick={reanalyzeAll}><Search size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.analyzeAll')} ({importedCount})</button>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('files.analyzeAllHint')}</span>
        </div>
      )}

      {/* ══ PESTAÑAS ══ Estimados primero, invoices después — el orden que pidió el dueño. Solo
          se dibujan las que tienen algo adentro, y siempre "Todos" (la historia cronológica
          completa, que sigue siendo como el equipo lee el claim). Con una sola pestaña con
          contenido no se dibuja nada: dos botones que llevan a la misma lista son ruido. */}
      {!uploadOnly && !estimatesOnly && pestanasVisibles.length > 2 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 12, borderBottom: '1px solid var(--line)', paddingBottom: 6 }}>
          {pestanasVisibles.map((x) => {
            const activa = x.id === pestanaActiva;
            const Icono = x.icon;
            return (
              <button key={x.id} className="btn ghost sm" onClick={() => { setPestana(x.id); setPestanaFijada(true); setResaltado(null); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--fs-2xs)',
                  fontWeight: activa ? 'var(--fw-bold)' : 'var(--fw-medium)',
                  color: activa ? 'var(--accent)' : 'var(--muted)',
                  background: activa ? 'var(--accent-soft)' : 'transparent',
                  borderColor: activa ? 'transparent' : 'transparent', borderRadius: 8, padding: '4px 10px',
                }}>
                <Icono size={13} strokeWidth={1.75} /> {t(x.labelKey)}
                <span style={{ opacity: 0.7, fontFamily: 'var(--font-mono)' }}>{conteos.get(x.id) ?? 0}</span>
              </button>
            );
          })}
        </div>
      )}

      {uploadOnly ? null : files.length === 0 ? (
        <div className="muted" style={{ marginTop: 12 }}>{t('files.empty')}</div>
      ) : (
        <div style={{ display: 'grid', gap: 5, marginTop: 12, alignItems: 'start' }}>
          {/* ⚠️ ORDEN CRONOLÓGICO, NO POR TIPO — pedido de Paula (2026-08-10): "me confunde mucho,
              se mira como Monday. Que todo sea en orden, like from when they uploaded it.
              Organizarlo por fecha y colocarle colores distintos por tipo de file: de arriba a
              abajo el más antiguo hasta llegar abajo al más nuevo, por fecha no por tipo".

              Antes se agrupaba por CATEGORÍA con encabezados. Eso obliga a reconstruir en la
              cabeza qué pasó primero: el contrato estaba en un bloque, la foto en otro y el
              estimado en otro, aunque hubieran llegado el mismo día. Leído como historia —que es
              como se usa— el orden de llegada es el que importa.

              El TIPO no se pierde: sigue en el borde de color de cada fila (ahora más marcado) y
              en el selector editable. Lo que se va es la AGRUPACIÓN, no la señal.

              Separador por DÍA para que el orden se lea de un vistazo en vez de tener que comparar
              fechas fila por fila. */}
          {/* LO MÁS NUEVO ARRIBA (pedido de Paula 2026-08-19: "I want to see the most recent
              files that have been uploaded first"). Un claim con 40 archivos importados obligaba
              a scrollear hasta el fondo para ver lo que se acaba de subir, que es justo lo que se
              está mirando cuando se abre la pestaña. Los separadores por día siguen igual, ahora
              del día más reciente hacia atrás. Desempate por id: dos archivos subidos en el mismo
              segundo (una carga múltiple) tienen que salir siempre en el mismo orden. */}
          {/* EN LA PESTAÑA DE ESTIMADOS EL ORDEN ES OTRO, y a propósito: la secuencia de la
              negociación. "Primero el insurance approval, después el counter offer, después
              venga el round two y así, bien organizado" (dueño, 2026-08-21). Ahí la fecha de
              subida no dice nada —un estimado del seguro puede subirse semanas después de la
              ronda que contesta— y el número de ronda sí. Sale de la MISMA fuente que la
              etiqueta (etiquetarEstimados), no de una segunda regla que después no cuadre.
              Ojo: en este modo NO se dibujan los separadores por día. Un separador de fecha
              sobre una lista que no está ordenada por fecha miente. */}
          {[...files]
            .sort((a, b) => {
              if (porSecuencia) {
                const d = etDe.ordenDe(a.id) - etDe.ordenDe(b.id);
                if (d !== 0) return estNuevoPrimero ? -d : d;
                // Mismo lugar en la secuencia (o los dos sin etiqueta): el más viejo primero,
                // que es el orden en que ocurrieron.
                const f2 = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                const rs = f2 !== 0 ? f2 : Number(a.id) - Number(b.id);
                return estNuevoPrimero ? -rs : rs;
              }
              const d = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
              const r = d !== 0 ? d : Number(b.id) - Number(a.id);
              return masNuevoPrimero ? r : -r;
            })
            .map((f, i, sorted) => {
            const cat = fileCategory(f);
            const meta = CAT_META[cat];
            const dia = (x: FileRow) => new Date(x.created_at).toDateString();
            const nuevoDia = !porSecuencia && (i === 0 || dia(sorted[i - 1]!) !== dia(f));
            const header = nuevoDia ? (
              <div key={`dia-${f.id}`} style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8, marginTop: i > 0 ? 10 : 0 }}>
                <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
                  {new Date(f.created_at).toLocaleDateString(localeUI(), { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
              </div>
            ) : null;
            const v = f.validation;
            const open = expandedIds.has(f.id);
            // El monto que se ve SIN abrir: el RCV si es estimado, el total del documento si no.
            const lineAmount = f.financials?.rcv_amount_cents ?? f.amount_cents;
            // DE DÓNDE SALIÓ, en una frase. Antes esto decía como mucho "subido por X" y solo si
            // había usuario; el resto de los archivos no decía nada de nada. Ahora sale de las
            // columnas origin_* (mig 0401), que se escriben al insertar.
            const llegada = comoLlego(f, t);
            const origin = llegada.texto;
            const IconoOrigen = llegada.icon;
            // Estimados POR COLOR de fondo (pedido 2026-08-10): el del seguro en rojo,
            // el de nosotros (Proactive/mitigation) en morado. El resto queda como estaba.
            const esEstimadoNuestro = f.doc_type === 'proactive_estimate' || f.doc_type === 'mitigation_estimate' || f.doc_type === 'rebuild_estimate';
            // Un estimado con PDF propio: a esos se les puede pedir la lectura única (un link externo no tiene archivo).
            const esLeible = ESTIMATE_TYPES.includes(f.doc_type) && !f.external_url;
            // Una PÓLIZA con PDF propio: se lee una vez (mig 0504) y el Coverage Detector trabaja sobre esa lectura.
            const esPoliza = POLICY_TYPES.includes(f.doc_type) && !f.external_url;
            const estBg = f.doc_type === 'estimate' ? 'var(--red-soft)'
              : (f.doc_type === 'proactive_estimate' || f.doc_type === 'mitigation_estimate') ? 'var(--purple-soft)'
              : null;
            return (
              <Fragment key={f.id}>
              {header}
              {/* id ancla: permite saltar a ESTE archivo desde una alerta urgente. */}
              <div id={`file-${f.id}`}
                {...(esEstimadoNuestro ? {
                  /* SOLTAR EL .ESX SOBRE LA FILA (Manuel 2026-09-28: "ponlo para drag and drop"). Se frena la
                     propagación para que no lo tome la zona general de arriba, que lo subiría como archivo
                     suelto y lo clasificaría aparte. Solo .esx: cualquier otra cosa avisa y no hace nada. */
                  onDragOver: (e: React.DragEvent) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy'; if (esxDrag !== f.id) setEsxDrag(f.id); } },
                  onDragLeave: (e: React.DragEvent) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setEsxDrag((v) => (v === f.id ? null : v)); },
                  onDrop: (e: React.DragEvent) => {
                    e.preventDefault(); e.stopPropagation(); setEsxDrag(null);
                    const file = e.dataTransfer.files?.[0];
                    if (!file) return;
                    // ¿Es un ESX? Por extensión, o por nombre, o un binario sin tipo conocido (Xactimate no
                    // declara MIME; Windows a veces esconde la extensión). Un PDF, imagen u Office NO.
                    const nombre = file.name || '';
                    const claramenteOtro = /\.(pdf|jpe?g|png|gif|heic|webp|docx?|xlsx?|csv|txt|zip|mov|mp4)$/i.test(nombre);
                    const pareceEsx = /\.esx$/i.test(nombre) || (/esx/i.test(nombre) && !claramenteOtro) || (!claramenteOtro && (!file.type || file.type === 'application/octet-stream'));
                    if (!pareceEsx) { flash(`${t('files.esxOnly')} (${nombre || file.type || '?'})`); return; }
                    void uploadEstimateEsx(f.id, file);
                  },
                } : {})}
                style={{
                border: esxDrag === f.id ? '2px dashed var(--orange)' : resaltado === f.id ? '2px solid var(--orange)' : '1px solid var(--line)',
                borderRadius: 9, borderLeft: `5px solid ${meta.color}`,
                background: resaltado === f.id ? 'var(--orange-soft, #fff7ed)' : open ? 'var(--surface-2)' : (estBg ?? 'var(--card)'),
                scrollMarginTop: 70, overflow: 'hidden',
              }}>
                {/* ══ LA LÍNEA ══ Todo lo que hace falta para decidir sin abrir nada: tipo, nombre,
                    monto, fecha y de dónde vino. Lo demás vive adentro (pedido 2026-08-03: las
                    tarjetas cuadradas con todo encima eran "mucho ruido visual"). */}
                {/* Columnas ELÁSTICAS. Con el select en 176px fijos, en un panel angosto (el drawer del
                    claim, una pantalla partida) la columna del nombre colapsaba a 0px y la fila se
                    partía en tres renglones — justo el ruido que se viene a sacar. Todo lleva
                    minmax(0, …) y min-width: 0 para que se encoja en vez de desbordar. */}
                <div style={{ display: 'grid', gridTemplateColumns: ' 20px minmax(84px, 176px) minmax(0, 1fr) auto', gap: 8, alignItems: 'center', padding: '6px 10px' }}>
                  <button className="btn ghost sm" onClick={() => setExpandedId(f.id)}
                    title={open ? t('files.collapse') : t('files.expand')}
                    style={{ padding: '0 4px', lineHeight: 1, minWidth: 22, fontSize: 'var(--fs-sm)', color: 'var(--muted)' }}>
                    {open ? '▾' : '▸'}
                  </button>

                  {/* Tipo EDITABLE: el clasificador se equivoca y hasta ahora no había forma de
                      corregirlo desde acá. Cambiarlo limpia la etiqueta vieja de la IA. */}
                  <select
                    className="input sm"
                    value={DOC_TYPE_KEY[f.doc_type] || customTypes.some((x) => x.value === f.doc_type) ? f.doc_type : 'other'}
                    disabled={!!busy}
                    onChange={async (e) => {
                      // "＋ Agregar tipo…": crea el tipo de la casa y se lo pone a ESTE archivo.
                      if (e.target.value === '__add__') {
                        const label = window.prompt(t('files.newTypePrompt'))?.trim();
                        if (!label) return;
                        try {
                          const r = await api.post<CustomType>('/api/files/doc-types', { label });
                          setCustomTypes((prev) => (prev.some((x) => x.value === r.value) ? prev : [...prev, r].sort((a, b) => a.label.localeCompare(b.label))));
                          changeDocType(f, r.value);
                        } catch (err) { alert(err instanceof ApiError ? err.message : 'Error'); }
                        return;
                      }
                      changeDocType(f, e.target.value);
                    }}
                    title={t('files.changeType')}
                    style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', padding: '3px 4px', height: 26, minWidth: 0, width: '100%', color: meta.color, background: `${meta.color}12`, borderColor: `${meta.color}44` }}
                  >
                    {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{t(d.key)}</option>)}
                    {customTypes.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                    {hasRole(me, 'manager') && <option value="__add__">＋ {t('files.newType')}</option>}
                  </select>

                  {/* Nombre + los tres datos que pidieron ver en la línea. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                    {/* El nombre cede espacio primero (se trunca); el monto, la fecha y el origen
                        no se aplastan porque son justamente lo que hay que leer sin abrir. */}
                    {editId === f.id ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder={t('files.namePh')} autoFocus
                          onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditId(null); }}
                          style={{ width: 240, padding: '3px 8px', border: '1px solid var(--line)', borderRadius: 7, font: 'inherit', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' }} />
                        <button className="btn sm" disabled={!!busy} onClick={saveEdit} style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent' }}><Check size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
                        <button className="btn ghost sm" onClick={() => setEditId(null)}><X size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
                      </span>
                    ) : (
                      <>
                        {/* Click en el NOMBRE = abrir el documento (lo más obvio tiene que ser
                            lo que la gente intenta primero). El ▸ de la izquierda sigue
                            expandiendo los detalles. */}
                        <span onClick={() => setViewer({ id: f.id, name: f.file_name })} title={f.file_name ?? ''}
                          style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 120px', minWidth: 0, textDecoration: 'underline', textDecorationColor: 'var(--line)', textUnderlineOffset: 3 }}>
                          {f.doc_label ? <span className="muted" style={{ fontWeight: 500 }}>{f.doc_label} · </span> : null}
                          {f.file_name || `file-${f.id}`}
                        </span>
                        <button className="btn ghost sm" onClick={() => startEdit(f)} title={t('files.rename')} style={{ padding: '0 5px', fontSize: 'var(--fs-2xs)', color: 'var(--muted)' }}><Pencil size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
                      </>
                    )}
                    {/* Chips de estado que SÍ merecen estar en la línea (hay que verlos de un vistazo). */}
                    {f.atr_status === 'approved' && <span className="badge green" style={{ fontSize: 'var(--fs-2xs)' }}><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.approved')}</span>}
                    {f.atr_status === 'rejected' && <span className="badge red" style={{ fontSize: 'var(--fs-2xs)' }}><X size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.rejected')}</span>}
                    {f.doc_type === 'coc' && v && (v.validated
                      ? <span className="badge green" style={{ fontSize: 'var(--fs-2xs)' }}><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.validated')}</span>
                      : <span className="badge red" style={{ fontSize: 'var(--fs-2xs)' }}><AlertTriangle size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.mismatch')}</span>)}
                    {/* En Files el chip del estimado del seguro dice "Approved" (no "Pre-approved"):
                        acá marca CUÁL estimado quedó aprobado, no la etapa del board (pedido 2026-08-10). */}
                    {f.supplement_status && (
                      <span className="badge green" style={{ fontSize: 'var(--fs-2xs)', display: 'inline-flex', alignItems: 'center', gap: 4 }} title={f.supplement_status.by ?? ''}>
                        <Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t(f.supplement_status.kind === 'resolved' ? 'sup.resolved' : 'files.approved')}
                        {(f.supplement_status.by_user_id || f.supplement_status.by) && <Avatar userId={f.supplement_status.by_user_id} name={f.supplement_status.by} size={15} />}
                      </span>
                    )}
                    {/* ¿ESTE ESTIMADO TIENE SU ESX? (pedido del dueño 2026-08-24: "a nuestros
                        files de estimados colocales un chip indicativo de que tiene el ESX file
                        subido"). El dato ya viajaba, pero solo se veía DESPLEGANDO la fila: para
                        saber si el .esx estaba había que abrir los detalles de cada estimado, uno
                        por uno. Es justo lo que se necesita de un vistazo — el ESX es el archivo
                        con el que se sigue trabajando el estimado en Xactimate, y sin él hay que
                        rehacerlo. Un clic en el chip lo baja, así que además ahorra el desplegable.

                        La condición es simplemente "tiene ESX": el backend solo llena ese campo
                        cuando hay uno enlazado, y en la práctica eso pasa en los estimados
                        NUESTROS —que son los únicos donde se ofrece subirlo—. Filtrar además por
                        doc_type sería adivinar: si un archivo tiene ESX, esconderlo por su tipo
                        oculta un dato que existe. */}
                    {f.esx && (
                      /* "ESX uploaded" + botón de descarga del .ESX solo (Manuel 2026-09-28: "en el item tiene
                         que decir ESX uploaded y un botón para descargar solo el ESX"). */
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--green-soft)', color: 'var(--green)', display: 'inline-flex', alignItems: 'center', gap: 3 }} title={f.esx.file_name || ''}>
                          <Check size={11} strokeWidth={2.5} /> {t('files.esxUploaded')}
                        </span>
                        <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)', padding: '1px 7px', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                          title={f.esx.file_name || t('files.esxDownload')}
                          onClick={async (e) => { e.stopPropagation(); const u = await urlFor(f.esx!.id); if (u) window.open(u, '_blank', 'noopener'); }}>
                          <Download size={11} strokeWidth={2} /> {t('files.esxDownload')}
                        </button>
                      </span>
                    )}
                    {/* LA ETIQUETA ÚNICA del estimado (ley 2026-08-19) — la MISMA fuente que
                        Review Estimate y el board (/negotiation/estimates → etiquetarEstimados),
                        con el AVATAR de quien lo hizo/etiquetó para saberlo sin abrir el archivo:
                        repair lleva a quien lo marcó (audit log); las demás al que subió. */}
                    {(() => {
                      const et = etDe(f.id);
                      if (!et) return null;
                      // El avatar dice QUIÉN es responsable de la etiqueta: repair → quien lo
                      // marcó (audit log; en una copia propagada sin marca propia NO se pinta
                      // al uploader como si hubiera marcado); las demás → quien lo subió.
                      const esRepair = et.tag === 'repair';
                      // El dropdown solo aplica al lado del SEGURO (ver setEstimateVersionRepair).
                      const esNuestro = ['proactive_initial', 'proactive', 'counter', 'mitigation'].includes(et.tag);
                      /**
                       * EL AVATAR DICE QUIÉN DECIDIÓ LA ETIQUETA, Y SI NO LA DECIDIÓ NADIE NO HAY
                       * AVATAR (Loc v Thai, 2026-10-21).
                       *
                       * Antes el avatar de cualquier etiqueta que no fuera repair era el del que
                       * SUBIÓ el archivo. En ese claim el seguro mandó un reemplazo PARCIAL, el
                       * claim estaba en Waiting approval y Files mostraba "Approved estimate" con
                       * la cara de quien había subido el PDF: la oficina entendió que esa persona
                       * lo había aprobado y se armó la confusión con los pagos. Nadie lo aprobó —
                       * `etiquetarEstimados` le pone `approved` al PRIMER estimado del seguro que
                       * no sea repair, porque lo usa de BASE para contar rondas.
                       *
                       * Entonces: etiqueta corregida a mano → la cara de quien la corrigió.
                       * Etiqueta derivada por la regla → NINGUNA cara, y el tooltip lo dice.
                       */
                      const tagPorPersona = f.financials?.tag_by_id ?? f.financials?.tag_by ?? null;
                      const avatarId = tagPorPersona ? (f.financials?.tag_by_id ?? null) : null;
                      const avatarName = tagPorPersona ? (f.financials?.tag_by ?? null) : null;
                      const icon = esRepair ? <Wrench size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />
                        : et.tag === 'proactive_initial' ? <Ruler size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> : null;
                      const cuando = f.financials?.tag_at ? new Date(f.financials.tag_at).toLocaleDateString(localeUI()) : null;
                      const title = tagPorPersona
                        ? `${t('files.tagSetBy')} ${f.financials?.tag_by ?? ''}${cuando ? ` · ${cuando}` : ''}`
                        : `${t('files.tagAutoWhy')}${f.uploaded_by ? `\n${t('files.uploadedBy')} ${f.uploaded_by}` : ''}`;
                      return (
                        <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: et.bg, color: et.fg, fontWeight: 'var(--fw-bold)', display: 'inline-flex', alignItems: 'center', gap: 4 }} title={title}>
                          {icon} {et.text}
                          {(avatarId || avatarName)
                            ? <Avatar userId={avatarId} name={avatarName} size={15} />
                            /* Sin cara: lo decidió la regla. El "auto" chiquito es lo que evita
                               que "Approved estimate" se lea como la decisión de una persona. */
                            : <span style={{ opacity: 0.65, fontWeight: 'var(--fw-normal)', fontSize: 'var(--fs-3xs, 9px)', letterSpacing: '.02em' }}>{t('files.tagAutoShort')}</span>}
                          {/* CORREGIR la etiqueta a mano (office+). Es un DESPLEGABLE y no una ✕
                              porque el pedido fue poder CAMBIARLAS en los dos sentidos: en Londa
                              Amis el repair y el approved estaban intercambiados. Solo en el lado
                              del seguro: "approved for repair" es una decisión sobre la respuesta
                              del carrier. Lo demás (Round N, counter N) lo sigue decidiendo la ley
                              por monto y orden — por eso la opción se llama "automático". */}
                          {/* CORREGIR LA ETIQUETA A MANO — ahora también el NÚMERO DE RONDA y
                              "no es una ronda" (pedido del dueño 2026-08-21: "que puedan cambiar a
                              repair o el round number"). Antes solo se podía marcar repair, y solo
                              del lado del seguro; un número corrido no había forma de arreglarlo.
                              Genora Harrell es el caso: su "Approved estimate" es el estimado de
                              un claim VIEJO subido a este file, y por ser el más antiguo se quedó
                              con la base y empujó los de 2026 a Round 2 y Round 3. Con "Not a
                              round" sale de la secuencia y los demás se renumeran solos. */}
                          {canApprove && (
                            <select className="input sm" disabled={!!busy} value={etDe.manualDe(f.id)}
                              title={t('files.tagHint')}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => { e.stopPropagation(); void setEstimateTag(f, e.target.value); }}
                              style={{ height: 18, padding: '0 2px', fontSize: 'var(--fs-2xs)', border: 'none', background: 'transparent', color: 'inherit', fontWeight: 700, cursor: 'pointer', maxWidth: 128 }}>
                              <option value="auto">{t('files.tagAuto')}</option>
                              {!esNuestro && <option value="repair">{t('files.repairEstimate')}</option>}
                              <option value="excluded">{t('files.tagNotARound')}</option>
                              {/* LAS QUE SUMAN AL RCV (dueño 2026-10-23): un estimado del seguro por el tarp, la
                                  mitigación o la inspección / ATR no es una ronda — se le SUMA al RCV aprobado
                                  (Payments lo toma). El repair, en cambio, no cuenta. Solo del lado del seguro. */}
                              {!esNuestro && <option value="tarp">{t('etag.tarp')}</option>}
                              {!esNuestro && <option value="mitigation">{t('etag.insMitigation')}</option>}
                              {!esNuestro && <option value="atr">{t('etag.atr')}</option>}
                              {/* Los números que se pueden poner: los que ya existen de ese lado y
                                  UNO más — para poder mandar un estimado a la ronda siguiente sin
                                  tener que crearla antes. Del seguro 1 = el approved (la base);
                                  nuestro 0 = el initial. */}
                              {(esNuestro
                                ? Array.from({ length: etDe.versiones('ours') + 1 }, (_, i) => i)
                                : Array.from({ length: etDe.versiones('insurance') + 1 }, (_, i) => i + 1)
                              ).map((n) => (
                                <option key={n} value={`n${n}`}>
                                  {esNuestro
                                    ? (n === 0 ? t('files.initialOurs') : t('etag.counter', { n: String(n) }))
                                    : (n === 1 ? t('etag.approved') : t('etag.round', { n: String(n) }))}
                                </option>
                              ))}
                            </select>
                          )}
                        </span>
                      );
                    })()}
                    {/* MONTO · FECHA · ORIGEN — los tres sin abrir nada. */}
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 'auto' }}>
                    {/* ESTADO DE LA LECTURA ÚNICA (mig 0498) sin abrir la fila: sin leer / leyendo con los
                        segundos corriendo / semáforo + partidas / falló. El chip listo abre la lectura. */}
                    {esLeible && (() => {
                      const rd = f.reading ?? null;
                      // SIN LEER = EL BOTÓN DE LEER (dueño 2026-10-08: "¿qué botón debo apretar?"): antes el chip era
                      // solo un letrero y el botón real vivía en la fila expandida; la lupa de al lado es otra cosa
                      // (clasificar + montos). Un clic acá encola la lectura y el chip pasa a "Leyendo…".
                      if (!rd) {
                        if (readOnly) return <span className="rd-chip" title={t('rd.readHint')}><span className="rd-dot" style={{ background: 'var(--line)' }} /> {t('rd.notRead')}</span>;
                        return (
                          <button type="button" className="rd-chip todo" disabled={rdPosting === f.id} title={t('rd.readHint')} onClick={(e) => { e.stopPropagation(); void leerEstimado(f, false); }}>
                            <ScanText size={11} strokeWidth={2} /> {t('rd.read')}
                          </button>
                        );
                      }
                      if (lecturaEnCurso(rd)) {
                        const s = rdSegundos(f);
                        return <span className="rd-chip" title={rd.status === 'queued' ? t('rd.queued') : t('rd.reading')}><span className="rd-spin" /> {t('rd.reading')}{s && <span className="rd-elapsed">{s}</span>}</span>;
                      }
                      if (rd.status === 'failed') {
                        if (readOnly) return <span className="rd-chip failed" title={t('rd.chipHintFailed', { err: rd.error ?? '' })}><AlertTriangle size={11} strokeWidth={2} /> {t('rd.failed')}</span>;
                        // Falló → el mismo chip reintenta (sin confirmación: no hay lectura que pisar).
                        return (
                          <button type="button" className="rd-chip failed" disabled={rdPosting === f.id} title={`${t('rd.chipHintFailed', { err: rd.error ?? '' })} · ${t('rd.retry')}`} onClick={(e) => { e.stopPropagation(); void leerEstimado(f, true); }}>
                            <AlertTriangle size={11} strokeWidth={2} /> {t('rd.failed')} · {t('rd.retry')}
                          </button>
                        );
                      }
                      return (
                        <>
                          <button type="button" className="rd-chip done" onClick={(e) => { e.stopPropagation(); setLectura({ fileId: f.id, fileName: f.file_name }); }}
                            title={`${t('rd.open')} · ${t('rd.chipHint', { model: rd.model ?? '—', s: fmtSegundos(rd.latency_ms), date: rd.read_at ? new Date(rd.read_at).toLocaleString(localeUI()) : '—' })}`}>
                            <span className="rd-dot" style={{ background: colorSemaforo(rd.semaforo) }} /> {t('rd.items', { n: rd.line_count ?? 0 })}
                          </button>
                          {/* RELEER (dueño 2026-10-30: "los estimados necesitan un botón para ser releídos para que agarren las medidas de los
                              cuartos y techos"): las lecturas viejas no traen las medidas por área. El generado no se relee (409 del servidor). */}
                          {!readOnly && f.source_tag !== 'Generated from Review Estimate' && (
                            <button type="button" className="rd-chip" disabled={rdPosting === f.id} title={t('rd.rereadHint')} onClick={(e) => { e.stopPropagation(); void leerEstimado(f, true); }}>
                              <RotateCcw size={11} strokeWidth={2} /> {t('rd.reread')}
                            </button>
                          )}
                        </>
                      );
                    })()}
                    {/* ESTADO DE LA LECTURA ÚNICA DE LA PÓLIZA (mig 0504): sin leer = el botón de leer; leyendo con los
                        segundos; semáforo + formularios (abre el Coverage Detector del claim); falló = reintenta. */}
                    {esPoliza && (() => {
                      const pz = f.policy_reading ?? null;
                      if (!pz) {
                        if (readOnly) return <span className="rd-chip" title={t('pz.readHint')}><span className="rd-dot" style={{ background: 'var(--line)' }} /> {t('rd.notRead')}</span>;
                        return (
                          <button type="button" className="rd-chip todo" disabled={pzPosting === f.id} title={t('pz.readHint')} onClick={(e) => { e.stopPropagation(); void leerPoliza(f, false); }}>
                            <ScanText size={11} strokeWidth={2} /> {t('pz.read')}
                          </button>
                        );
                      }
                      if (lecturaPolizaEnCurso(pz)) {
                        const desde = pzInicio.current[f.id] ?? (pz.started_at ? Date.parse(pz.started_at) : null);
                        const s = desde ? fmtSegundos(Date.now() - desde) : null;
                        return <span className="rd-chip" title={pz.status === 'queued' ? t('pz.queued') : t('pz.reading')}><span className="rd-spin" /> {t('pz.reading')}{s && <span className="rd-elapsed">{s}</span>}</span>;
                      }
                      if (pz.status === 'failed') {
                        if (readOnly) return <span className="rd-chip failed" title={t('pz.chipHintFailed', { err: pz.error ?? '' })}><AlertTriangle size={11} strokeWidth={2} /> {t('pz.failed')}</span>;
                        return (
                          <button type="button" className="rd-chip failed" disabled={pzPosting === f.id} title={`${t('pz.chipHintFailed', { err: pz.error ?? '' })} · ${t('pz.retry')}`} onClick={(e) => { e.stopPropagation(); void leerPoliza(f, true); }}>
                            <AlertTriangle size={11} strokeWidth={2} /> {t('pz.failed')} · {t('pz.retry')}
                          </button>
                        );
                      }
                      return (
                        <a className="rd-chip done" href={`/coverage-detector?claim=${encodeURIComponent(claimId)}${pz.pr_policy_id ? `&policy=${encodeURIComponent(pz.pr_policy_id)}` : ''}`} onClick={(e) => e.stopPropagation()}
                          title={`${t('pz.open')} · ${t('rd.chipHint', { model: pz.model ?? '—', s: fmtSegundos(pz.latency_ms), date: pz.read_at ? new Date(pz.read_at).toLocaleString(localeUI()) : '—' })}`}>
                          <span className="rd-dot" style={{ background: colorSemaforo(pz.semaforo) }} /> {t('pz.done', { n: pz.form_count ?? 0 })}
                        </a>
                      );
                    })()}
                    {/* Analizar con IA UNO a UNO sin expandir la fila (pedido 2026-08-17). Para los ESTIMADOS ya no
                        (dueño 2026-10-08: "quítame la lupa, ahora va a ser el parse"): la lectura única da todo. */}
                    {!f.external_url && !esLeible && (
                      <button className="btn ghost sm" disabled={!!busy} title={t('files.analyzeHint')} onClick={() => reanalyze(f)} style={{ padding: '0 6px', color: 'var(--accent)' }}>
                        <Search size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />
                      </button>
                    )}
                    {/* El MONTO se corrige con un click (la IA a veces lo lee mal). */}
                    {amtEditId === f.id ? (
                      <input className="input sm" autoFocus value={amtEditVal} onChange={(e) => setAmtEditVal(e.target.value)}
                        style={{ width: 110, fontFamily: 'var(--font-mono)' }}
                        onBlur={() => void saveFileAmount(f)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void saveFileAmount(f); if (e.key === 'Escape') setAmtEditId(null); }} />
                    ) : lineAmount != null ? (
                      <span role="button" tabIndex={0} onClick={() => { setAmtEditId(f.id); setAmtEditVal((lineAmount / 100).toFixed(2)); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setAmtEditId(f.id); setAmtEditVal((lineAmount / 100).toFixed(2)); } }}
                        style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', color: 'var(--green)', background: 'var(--green-soft)', fontFamily: 'var(--font-mono)', borderRadius: 7, padding: '1px 8px', whiteSpace: 'nowrap', cursor: 'pointer', borderBottom: '1px dashed var(--green)' }}
                        title={`${f.financials?.rcv_amount_cents != null ? 'RCV' : t('files.docTotal')} — ${t('files.amountEditHint')}`}>
                        {formatUsd(lineAmount)}
                      </span>
                    ) : (ESTIMATE_TYPES.includes(f.doc_type) && (
                      <button className="btn ghost sm" disabled={!!busy} title={t('files.amountEditHint')} style={{ fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)' }}
                        onClick={() => { setAmtEditId(f.id); setAmtEditVal(''); }}>$ …</button>
                    ))}
                    <span className="muted" style={{ fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap' }}>{new Date(f.created_at).toLocaleDateString(localeUI())}</span>
                    {origin && (
                      <span className="muted" style={{ fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 190, display: 'inline-flex', alignItems: 'center', gap: 4 }} title={origin}>
                        <IconoOrigen size={11} strokeWidth={1.75} style={{ flexShrink: 0 }} /> {origin}
                      </span>
                    )}
                    </span>
                  </div>

                  {/* Acciones de la fila. Lo raro (reemplazar, analizar) se fue adentro. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    {f.external_url ? (
                      <a className="btn sm" href={f.external_url} target="_blank" rel="noopener noreferrer"
                        style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent', padding: '2px 9px', fontSize: 'var(--fs-2xs)' }}><Play size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.view')}</a>
                    ) : (
                      <button className="btn sm" onClick={() => setViewer({ id: f.id, name: f.file_name })} title={t('files.view')}
                        style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent', padding: '2px 9px', fontSize: 'var(--fs-2xs)' }}><Eye size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.view')}</button>
                    )}
                    {/* ESTIMAR SOBRE ESTE ESTIMADO (dueño 2026-10-30): abre el estimado leído —nuestro o del seguro— como borrador en la
                        Estimating Tool para borrar o agregar ítems. Sin lectura terminada, el botón lo dice (el chip "Not read" la pide). */}
                    {ESTIMATE_TYPES.includes(f.doc_type) && !readOnly && (
                      <button className="btn sm" disabled={!!busy || f.reading?.status !== 'done'}
                        title={f.reading?.status === 'done' ? t('files.estimateFromHint') : t('files.estimateNeedsReading')}
                        onClick={(e) => { e.stopPropagation(); void estimarDesde(f); }}
                        style={{ padding: '2px 9px', fontSize: 'var(--fs-2xs)', background: 'var(--accent-soft)', color: 'var(--accent)', borderColor: 'transparent', fontWeight: 'var(--fw-bold)' }}>
                        <Calculator size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.estimateFrom')}
                      </button>
                    )}
                    {estErr?.id === f.id && <span style={{ color: 'var(--red)', fontSize: 'var(--fs-2xs)' }}>{estErr.text}</span>}
                    {/* EL .ESX DEL ESTIMADO, EN LA MISMA FILA (Manuel 2026-09-28: "un botón claro donde pueda
                        subir el ESX de ese estimado en el mismo item sin crear uno repetido"). Se cuelga del
                        estimado (parent_file_id) por /api/files/:id/estimate-esx, así que no aparece como
                        archivo suelto y reemplaza al anterior si ya había uno. Antes solo estaba adentro del
                        desplegable. Naranja si falta, verde si ya está. */}
                    {esEstimadoNuestro && (
                      <label className="btn sm" onClick={(e) => e.stopPropagation()} title={`${f.esx ? `${t('files.esxReplace')} · ${f.esx.file_name ?? ''}` : t('files.esxUpload')} · ${t('files.esxDropHint')}`}
                        style={{ cursor: busy ? 'wait' : 'pointer', padding: '2px 9px', fontSize: 'var(--fs-2xs)', borderColor: 'transparent',
                          background: f.esx ? 'var(--green-soft)' : 'var(--orange-soft)', color: f.esx ? 'var(--green)' : 'var(--orange)', fontWeight: 'var(--fw-bold)' }}>
                        <Puzzle size={12} strokeWidth={2} style={{ verticalAlign: 'text-bottom' }} /> {f.esx ? 'ESX ✓' : t('files.esxUpload').replace(/^⬆\s*/, '')}
                        <input type="file" accept=".esx,.ESX" style={{ display: 'none' }} disabled={!!busy}
                          onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) void uploadEstimateEsx(f.id, file); }} />
                      </label>
                    )}
                    {/* LA ZONA VISIBLE (Manuel 2026-09-28: "visualmente no lo veo"): un cuadro punteado que dice
                        dónde soltar. Siempre a la vista mientras falte el ESX; con ESX, solo mientras se arrastra. */}
                    {esEstimadoNuestro && (!f.esx || esxDrag === f.id) && (
                      <span style={{ border: `1.5px dashed ${esxDrag === f.id ? 'var(--orange)' : 'var(--muted)'}`, borderRadius: 7, padding: '2px 8px',
                        fontSize: 'var(--fs-2xs)', color: esxDrag === f.id ? 'var(--orange)' : 'var(--muted)', background: esxDrag === f.id ? 'var(--orange-soft)' : 'transparent',
                        display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                        <Puzzle size={11} strokeWidth={1.75} /> {t('files.esxDropHere')}
                      </span>
                    )}
                    {(() => {
                      const hidden = (f.partner_visibility ?? 'shared') === 'internal';
                      return (
                        <button className="btn ghost sm" disabled={!!busy} onClick={() => togglePartnerVisibility(f)}
                          title={hidden ? t('files.shareCoHint') : t('files.hideCoHint')}
                          style={{ padding: '2px 6px', fontSize: 'var(--fs-sm)', color: hidden ? 'var(--muted)' : 'var(--green)' }}>
                          {hidden ? <Ban size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> : <HardHat size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />}
                        </button>
                      );
                    })()}
                    {/* EL CLIENTE LO VE / NO LO VE. Solo se pinta con el portal encendido. El
                        motivo explica por qué un archivo no se ve cuando la casilla no alcanza:
                        tipo no tildado en Settings, tipo prohibido, genérico de la empresa. */}
                    {f.portal && (() => {
                      const p = f.portal;
                      const bloqueado = !p.visible && p.motivo !== null && p.motivo !== 'no_marcado';
                      const texto = p.visible ? t('files.portalVisible')
                        : t(`files.portalWhy.${p.motivo ?? 'no_marcado'}` as Parameters<typeof t>[0]);
                      return (
                        <button className="btn ghost sm" disabled={!!busy || bloqueado}
                          onClick={() => void togglePortalVisibility(f)}
                          title={`${texto}${p.manual ? ` · ${t('files.portalManual')}` : ''}`}
                          style={{ padding: '2px 6px', fontSize: 'var(--fs-sm)',
                            color: p.visible ? 'var(--accent)' : 'var(--muted)',
                            opacity: bloqueado ? 0.45 : 1 }}>
                          {p.visible
                            ? <CheckSquare size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />
                            : <Square size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />}
                        </button>
                      );
                    })()}
                    {/* COMPARTIR POR CORREO (pedido del dueño 2026-09-12): al contratista o al homeowner,
                        desde claims@, con el archivo adjunto y el asunto en inglés con su nombre. */}
                    {!f.external_url && (
                      <>
                        <button className="btn ghost sm" disabled={!!busy} onClick={() => compartir(f, 'contractor')} title={t('files.emailContractor')}
                          style={{ padding: '2px 6px', fontSize: 'var(--fs-2xs)', color: 'var(--orange)' }}><Mail size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> CO</button>
                        <button className="btn ghost sm" disabled={!!busy} onClick={() => compartir(f, 'homeowner')} title={t('files.emailHomeowner')}
                          style={{ padding: '2px 6px', fontSize: 'var(--fs-2xs)', color: 'var(--accent)' }}><Mail size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> HO</button>
                      </>
                    )}
                    {/* ARCHIVAR — lo que casi siempre se quiere cuando algo sobra: sacarlo de la
                        vista sin arriesgar el documento. La ✕ (borrar de verdad) se queda al
                        lado para quien sabe que quiere borrar. */}
                    <button className="btn ghost sm" disabled={!!busy} onClick={() => archivar(f)} title={t('files.archiveHint')}
                      style={{ padding: '2px 6px', fontSize: 'var(--fs-2xs)', color: 'var(--muted)' }}><Archive size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
                    <button className="btn ghost sm" onClick={() => del(f.id)} title={t('files.delete')} style={{ padding: '2px 6px', fontSize: 'var(--fs-2xs)', color: 'var(--muted)' }}><X size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
                  </div>
                </div>

                {/* ══ EL DETALLE ══ Todo lo que antes vivía encima de la tarjeta. */}
                {open && (
                  <div style={{ borderTop: '1px solid var(--line)', padding: '9px 12px 11px', display: 'grid', gap: 9 }}>
                    {f.financials && (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {f.financials.rcv_amount_cents != null && <FinPill label="RCV" value={formatUsd(f.financials.rcv_amount_cents)} bg="var(--accent-soft)" fg="var(--accent)" />}
                        {!f.financials.is_ours && f.financials.deductible_cents != null && <FinPill label={t('files.deductible')} value={formatUsd(f.financials.deductible_cents)} bg="var(--orange-soft)" fg="var(--orange)" />}
                        {!f.financials.is_ours && f.financials.depreciation_cents != null && <FinPill label={t('files.depreciation')} value={formatUsd(f.financials.depreciation_cents)} bg="#f1f5f9" fg="#334155" />}
                        {!f.financials.is_ours && f.financials.net_claim_cents != null && <FinPill label={t('files.netClaim')} value={formatUsd(f.financials.net_claim_cents)} bg="var(--green-soft)" fg="var(--green)" />}
                        {!f.financials.is_ours && f.financials.building_code && <FinPill label={t('files.buildingCode')} value={f.financials.building_code} bg="#f5f3ff" fg="#6d28d9" />}
                        {f.financials.price_list && (
                          f.financials.price_list_current === false
                            ? <FinPill label={t('files.priceList')} value={`${f.financials.price_list} · ${t('files.plOutdated', { n: String(f.financials.price_list_months_old ?? 0) })}`} bg="var(--red-soft)" fg="var(--red)" />
                            : f.financials.price_list_current === true
                              ? <FinPill label={t('files.priceList')} value={`${f.financials.price_list} · ${t('files.plCurrent')}`} bg="var(--green-soft)" fg="var(--green)" />
                              : <FinPill label={t('files.priceList')} value={f.financials.price_list} bg="#f1f5f9" fg="#334155" />
                        )}
                      </div>
                    )}
                    {f.financials && !f.financials.is_ours && (
                      <div>
                        {f.financials.roof_full_replacement === true && <div style={{ fontSize: 'var(--fs-2xs)', marginBottom: 6, background: 'var(--green-soft)', border: '1px solid #86efac', borderRadius: 6, padding: '6px 8px', color: 'var(--green)' }}><CheckCircle2 size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('sup.roofFull')}</div>}
                        {f.financials.roof_full_replacement === false && <div style={{ fontSize: 'var(--fs-2xs)', marginBottom: 6, background: 'var(--orange-soft)', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 8px', color: 'var(--orange)' }}><Wrench size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('sup.roofPartial')}</div>}
                        {canApprove && (
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button className="btn sm" disabled={!!busy} style={f.financials.roof_full_replacement === true ? { background: 'var(--good-fill)', color: 'var(--good-fill-ink)' } : undefined} onClick={approveFull}>{t('hero.approved')}</button>
                            <button className="btn sm" disabled={!!busy} style={f.financials.roof_full_replacement === false ? { background: 'var(--warn-fill)', color: 'var(--warn-fill-ink)' } : undefined} onClick={approveRepair}>{t('hero.approvedRepair')}</button>
                            <button className="btn sm" disabled={!!busy} style={{ background: 'var(--accent)', color: '#fff', borderColor: 'transparent' }} onClick={sendToReview}><ClipboardList size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('sup.sendToReview')}</button>
                          </div>
                        )}
                      </div>
                    )}
                    {/* CÓMO LLEGÓ, completo. En la línea colapsada va recortado a 190px; acá
                        entero, y con el link al correo del que salió — la pregunta "¿esto de
                        dónde salió?" se contesta yendo al email, no adivinando por el nombre. */}
                    <div className="muted" style={{ fontSize: 'var(--fs-2xs)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 'var(--fw-bold)' }}>{t('files.howArrived')}</span>
                      <IconoOrigen size={12} strokeWidth={1.75} /> <span>{origin}</span>
                      <span>· {new Date(f.created_at).toLocaleString('en-US')}</span>
                      {llegada.emailId && (
                        <a href={`/claims/${claimId}?tab=email#email-${llegada.emailId}`}
                          style={{ color: 'var(--accent)', fontWeight: 'var(--fw-semibold)' }}>{t('files.openEmail')}</a>
                      )}
                      {f.es_generico && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}><Building2 size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.casaDoc')}</span>}
                    </div>
                    {f.ai_summary && <div className="muted" style={{ fontSize: 'var(--fs-xs)', lineHeight: 1.55 }}>{f.ai_summary}</div>}
                    {f.doc_type === 'estimate' && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('files.estimateNote')}</div>}
                    {f.doc_type === 'coc' && v && !v.validated && v.diffs.length > 0 && (
                      <div style={{ fontSize: 'var(--fs-2xs)' }}>
                        {v.diffs.map((d) => (
                          <div key={d.field} style={{ color: 'var(--red)' }}>
                            {d.field}: <b>{d.doc || '—'}</b> {t('files.vsClaim')} <b>{d.claim || '—'}</b>
                          </div>
                        ))}
                      </div>
                    )}
                    {f.notes && (
                      <div style={{ fontSize: 'var(--fs-xs)', whiteSpace: 'pre-wrap', background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 8, padding: '7px 9px' }}>
                        <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', marginBottom: 3 }}><FilePenLine size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('atr.notes')}</div>
                        {f.notes}
                        <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 3 }}>{f.notes_by ?? ''}{f.notes_at ? ` · ${new Date(f.notes_at).toLocaleString('en-US')}` : ''}</div>
                      </div>
                    )}
                    {/* Acciones de segunda línea: acá abajo dejan de competir por atención. */}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      {!f.external_url && (
                        <label className="btn ghost sm" title={t('files.replace')} style={{ fontSize: 'var(--fs-2xs)', cursor: 'pointer' }}>
                          <RotateCcw size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.replace')}
                          <input type="file" accept="application/pdf,image/*,.esx,.ESX" style={{ display: 'none' }}
                            onChange={(e) => { const file = e.target.files?.[0]; if (file) replaceFileUpload(f, file); e.target.value = ''; }} />
                        </label>
                      )}
                      {/* Analizar UNO. Antes solo salía en los IMPORTADOS, y el que más lo
                          necesita es el que se subió por la app y quedó sin monto leído: el
                          estimado de Maria Nunez estaba ahí, con su PDF, y el bono lo contaba
                          como "un solo estimado" porque nadie podía dispararle la lectura. El
                          backend nunca pidió que fuera importado. Los links externos no tienen
                          archivo que leer. */}
                      {!f.external_url && !esLeible && (
                        <button className="btn sm" style={{ background: 'var(--accent)', color: '#fff', borderColor: 'transparent', fontSize: 'var(--fs-2xs)' }} disabled={!!busy} onClick={() => reanalyze(f)} title={t('files.analyzeHint')}><Search size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.analyze')}</button>
                      )}
                      {/* USAR EN PAYMENTS — le saca los números a ESTE archivo sin volver a
                          subirlo. Sale en los estimados (los del seguro son los que Payments
                          lee para definir el monto de la invoice) y en las invoices/documentos
                          con montos, que es lo otro que estaban re-subiendo. */}
                      {!f.external_url && (ESTIMATE_TYPES.includes(f.doc_type) || f.doc_type === 'rebuild_estimate') && (
                        <button className="btn sm" style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent', fontSize: 'var(--fs-2xs)' }}
                          disabled={!!busy} onClick={() => usarEnPayments(f)} title={t('files.usePaymentsHint')}>
                          <Calculator size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.usePayments')}
                        </button>
                      )}
                      {/* LEER EL ESTIMADO (mig 0498): la lectura única con IA, con los segundos a la vista —el
                          dueño quiere "probarlos a ver qué tan rápido"—. Releer pide confirmación porque pisa
                          la lectura que ya está. Sin botón en solo lectura (Previous claim files): ahí no se
                          dispara nada sobre el claim viejo. */}
                      {esLeible && !readOnly && (() => {
                        const rd = f.reading ?? null;
                        const enCurso = lecturaEnCurso(rd);
                        const listo = rd?.status === 'done';
                        // El GENERADO desde Review Estimate nace con su lectura en código: no se relee con el modelo (el servidor contesta 409).
                        const generado = f.source_tag === 'Generated from Review Estimate';
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            {!(listo && generado) && <button className="btn sm" style={{ background: 'var(--accent)', color: '#fff', borderColor: 'transparent', fontSize: 'var(--fs-2xs)' }}
                              disabled={enCurso || rdPosting === f.id} title={t('rd.readHint')} onClick={() => void leerEstimado(f, listo)}>
                              {enCurso ? <span className="rd-spin" style={{ borderTopColor: '#fff', verticalAlign: 'text-bottom' }} /> : <ScanText size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />}
                              {' '}{enCurso ? `${t('rd.reading')} ${rdSegundos(f) ?? ''}` : listo ? t('rd.reread') : t('rd.read')}
                            </button>}
                            {listo && rd && (
                              <>
                                <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                                  {rd.copied ? t('rd.copied') : t('rd.readIn', { s: fmtSegundos(rd.latency_ms), model: rd.model ?? '—' })}
                                </span>
                                <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={() => setLectura({ fileId: f.id, fileName: f.file_name })}>{t('rd.open')}</button>
                              </>
                            )}
                            {rd?.status === 'failed' && rd.error && <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--red)' }}>{rd.error}</span>}
                          </span>
                        );
                      })()}
                      {looksLikeContract(f) && (
                        <button className="btn sm" style={{ background: '#0284c7', color: '#fff', borderColor: 'transparent', fontSize: 'var(--fs-2xs)' }} disabled={!!busy} onClick={() => analyzeAsContract(f)} title={t('files.analyzeContractHint')}><Bot size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.analyzeContract')}</button>
                      )}
                      {(f.doc_type === 'proactive_estimate' || f.doc_type === 'mitigation_estimate') && (
                        <>
                          <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' }}><Puzzle size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> ESX</span>
                          {f.esx
                            ? <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={async () => { const u = await urlFor(f.esx!.id); if (u) window.open(u, '_blank', 'noopener'); }}><Download size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {f.esx.file_name || t('files.esxDownload')}</button>
                            : <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('files.esxNone')}</span>}
                          <FileDrop onFile={(file) => uploadEstimateEsx(f.id, file)} accept=".esx,.ESX" label={f.esx ? t('files.esxReplace') : t('files.esxUpload')} busy={!!busy} busyLabel={busy ?? ''} compact />
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
              </Fragment>
            );
          })}
        </div>
      )}
      {/* ══ EL CAJÓN DE ARCHIVADOS ══ Lo que se sacó de la lista sigue acá, con quién lo sacó y
          por qué, y vuelve con un click. Que exista este cajón es lo que hace segura la limpieza:
          sin él, "archivar" sería un borrado con otro nombre y nadie lo apretaría. Va plegado y
          al pie porque es el sótano, no la sala. */}
      {!uploadOnly && !estimatesOnly && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--line)', paddingTop: 8 }}>
          <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)', color: 'var(--muted)' }}
            onClick={() => { const v = !verArchivados; setVerArchivados(v); if (v && archivados == null) void cargarArchivados(); }}>
            {verArchivados ? '▾' : '▸'} <Archive size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.archivedDrawer')}
            {archivados != null && <span style={{ fontFamily: 'var(--font-mono)', marginLeft: 4 }}>{archivados.length}</span>}
          </button>
          {verArchivados && (
            archivados == null ? <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 6 }}>…</div>
              : archivados.length === 0 ? <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 6 }}>{t('files.archivedEmpty')}</div>
                : (
                  <div style={{ display: 'grid', gap: 3, marginTop: 6 }}>
                    {archivados.map((a) => (
                      <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-2xs)', padding: '4px 8px', background: 'var(--surface-2)', borderRadius: 7 }}>
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.file_name ?? ''}>{a.file_name || `file-${a.id}`}</span>
                        {a.archivado_motivo && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{MOTIVO_KEY[a.archivado_motivo] ? t(MOTIVO_KEY[a.archivado_motivo]!) : a.archivado_motivo}</span>}
                        {/* Sin persona = lo archivó la LIMPIEZA, no alguien. Decir "lo generó la app" acá sería
                            contestar otra pregunta: esa frase es sobre de dónde salió el archivo, no sobre
                            quién lo sacó de la lista. */}
                        <span className="muted" style={{ whiteSpace: 'nowrap' }}>{a.archivado_by ?? t('files.archivedByCleanup')} · {new Date(a.archivado_at).toLocaleDateString(localeUI())}</span>
                        <button className="btn ghost sm" disabled={!!busy} onClick={() => desarchivar(a.id)} title={t('files.unarchiveHint')}
                          style={{ padding: '1px 6px', fontSize: 'var(--fs-2xs)', color: 'var(--accent)' }}>
                          <ArchiveRestore size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('files.unarchive')}
                        </button>
                      </div>
                    ))}
                  </div>
                )
          )}
        </div>
      )}
      </>}
      {/* Visor a pantalla completa: muestra el documento o el ERROR (antes un fallo era silencio). */}
      {viewer && <FileViewerModal fileId={viewer.id} title={viewer.name} onClose={() => setViewer(null)} />}
      {/* La lectura única del estimado, entera (va por portal: ver EstimateReadingModal). */}
      {lectura && <EstimateReadingModal fileId={lectura.fileId} fileName={lectura.fileName} onClose={() => setLectura(null)} onChanged={() => { void load(); }} />}
    </div>
  );
}
